/**
 * SettingsPage — twenty cards, grouped into sub-tabs. Company-scoped, UI + CRUD
 * only; no money math.
 *
 * It was one scrolling page, which was right at six cards and is not at twenty:
 * finding the fee definitions meant scrolling past the arrival windows, and
 * nothing on screen said whether you had already gone past the thing you wanted.
 *
 * ---------------------------------------------------------------------------
 * EACH CARD DECLARES ITS OWN TAB, IN PLACE
 * ---------------------------------------------------------------------------
 * The cards stay in their existing order inside the existing admin block, each
 * wrapped in `show('<tab>') && …`. The alternative — cutting twenty cards out
 * and re-nesting them under seven parents — is a large JSX move for no
 * behavioural gain, and it would have lifted every card out of the one
 * `isAdmin &&` guard that keeps a rep from seeing company settings. The guard
 * is load-bearing and server-mirrored; it stays exactly where it is.
 *
 * ---------------------------------------------------------------------------
 * THE GROUPING
 * ---------------------------------------------------------------------------
 *   General       My Account · Company Info · Email signature · Email templates
 *   Users         Sales Reps · Role Assignments
 *   Pricing       Tariffs · Fee Definitions · Branches & Tax Rates · DVP ·
 *                 Payment Methods · Expense Categories
 *   Documents     Document templates
 *   Leads         Lead sources (intake providers) · Lead source options ·
 *                 Arrival Windows
 *   Contractors   Contractors · Contractor Terms
 *   Integrations  Email sync · Inventory photos & video
 *
 * Branches sits under Pricing because its tax rate drives every estimate total,
 * which is what sends someone looking for it. Payment methods and expense
 * categories are config_lists like the lead-source list, but they configure
 * money rather than intake, so they follow what they configure rather than how
 * they are stored.
 *
 * "Lead source options" is the config_list of dropdown values. "Lead sources"
 * is the L1 intake providers, with slugs and secret keys. Both were called
 * "Lead Sources" and that was survivable while ten cards sat between them; on
 * one tab it is not.
 *
 * Eighteen of the twenty cards are admin-only, so a rep gets the General tab
 * alone — with My Account and Email templates on it. The tab BAR is hidden for
 * them rather than shown with six tabs they cannot open, and a URL naming an
 * admin tab falls back to General instead of rendering a blank page.
 */

import React, { useMemo, useState } from 'react';
import { CRM } from '../lib/theme';
import { numberInput, STEP_PER_LB } from '../lib/numberInput';
import { humanize } from '../lib/feedLabels';
import { formatPhone } from '../lib/format';
import { useAuth } from '../contexts/AuthContext';
import { useApi, useApiClient } from '../hooks/useApi';
import { TemplateEditor } from './TemplateEditor';
import { AccessorialRatesEditor } from './AccessorialRatesEditor';
import { CrewPresetsEditor } from './CrewPresetsEditor';
import { supabaseClient } from '../services/supabase';
import { TZ_OPTIONS, userTz, tzCityLabel } from '../lib/time';
import { formatDateTime } from '../lib/utils';
import { MERGE_FIELDS, SMS_MERGE_FIELDS, SAMPLE_TEXT_VALUES, fillTextTemplate } from '../lib/mergeFields';
import { LeadSourcesCard } from './LeadSourcesCard';
import { Icons } from '../lib/icons';
import { prepare, search as searchCatalog } from '../lib/inventorySearch';
import { useAction } from '../hooks/useAction';
import { usePhoneWidth } from '../hooks/usePhoneWidth';
import { ringCentralEnabled } from '../lib/ringcentral';

/**
 * The sub-tab lives in the URL (/settings?tab=pricing), owned by App, exactly
 * as the lead file's sub-tab is — so a refresh, a back button or a pasted link
 * all land on the tab you were looking at.
 */
export const SETTINGS_TABS = ['general', 'users', 'pricing', 'documents', 'leads', 'contractors', 'integrations'] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

export const SETTINGS_TAB_LABEL: Record<SettingsTab, string> = {
  general: 'General',
  users: 'Users',
  pricing: 'Pricing & billing',
  documents: 'Documents',
  leads: 'Leads',
  contractors: 'Contractors',
  integrations: 'Integrations',
};

/** The tab named by ?tab=, or General. Used by the initial load AND popstate. */
export function settingsTabFromQuery(raw: string | null): SettingsTab {
  return (SETTINGS_TABS as readonly string[]).includes(raw ?? '') ? (raw as SettingsTab) : 'general';
}

const T = { ...CRM, mono: "'DM Mono', monospace" };

const money = (v: unknown) => `$${Number(v ?? 0).toFixed(2)}`;
const num = (v: unknown) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const cardStyle: React.CSSProperties = { background: T.panel, border: `1px solid ${T.border}`, borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column' };
const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '10px', background: T.panel2, borderRadius: '7px', padding: '9px 11px', marginBottom: '6px' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '7px 9px', background: T.panel2, border: `1px solid ${T.border}`, color: T.text, borderRadius: '6px', fontSize: '12px' };
const iconBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: T.muted, padding: '0 2px' };
const plusBtn: React.CSSProperties = { width: '26px', height: '26px', background: T.accentFill, color: 'var(--crm-on-fill)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '15px', lineHeight: 1 };
const primaryBtn: React.CSSProperties = { padding: '8px 14px', background: T.accentFill, color: 'var(--crm-on-fill)', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 };
const ghostBtn: React.CSSProperties = { padding: '8px 14px', background: T.panel2, color: T.muted, border: `1px solid ${T.border}`, borderRadius: '7px', cursor: 'pointer', fontSize: '12px' };

function Card({ Icon, title, onAdd, full, children }: { Icon: React.ComponentType<any>; title: string; onAdd?: () => void; full?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ ...cardStyle, gridColumn: full ? '1 / -1' : 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: T.text }}>
          <Icon size={16} aria-hidden="true" />{title}
        </div>
        {onAdd && <button onClick={onAdd} style={plusBtn} title={`Add ${title}`}>+</button>}
      </div>
      {children}
    </div>
  );
}

const labelRow: React.CSSProperties = { fontSize: '11px', color: T.muted, fontWeight: 500 };

const empty = (t: string) => <div style={{ fontSize: '12px', color: T.dim, padding: '4px 0' }}>{t}</div>;

export function SettingsPage({ activeTab = 'general', onTabChange }: {
  activeTab?: SettingsTab;
  onTabChange?: (tab: SettingsTab) => void;
} = {}) {
  const { user, logout } = useAuth();
  const api = useApiClient();
  // Phone: the top bar has no room for your name and Log out, so they are here.
  const phone = usePhoneWidth();
  // FIX C: Settings is no longer admin-gated. Everyone reaches My Account
  // (personal); company-level cards are admin-only (hidden here + server-guarded).
  const isAdmin = user?.role === 'admin';

  // Company-level data is only needed for the admin cards — skip for reps (some
  // of these endpoints are admin-only and would 403).
  const { data: users, refetch: refetchUsers } = useApi<any[]>(async () => (await api.getUsers()).data || [], { skip: !isAdmin });
  const { data: companyArr, refetch: refetchCompany } = useApi<any[]>(async () => (await api.getCompany()).data || [], { skip: !isAdmin });
  const { data: branches, refetch: refetchBranches } = useApi<any[]>(async () => (await api.getBranches()).data || [], { skip: !isAdmin });
  const { data: tariffs, refetch: refetchTariffs } = useApi<any[]>(async () => (await api.getTariffs()).data || [], { skip: !isAdmin });
  const { data: fees, refetch: refetchFees } = useApi<any[]>(async () => (await api.getFees()).data || [], { skip: !isAdmin });
  const { data: contractors, refetch: refetchContractors } = useApi<any[]>(async () => (await api.getContractors()).data || [], { skip: !isAdmin });

  const [modal, setModal] = useState<{ kind: 'branch' | 'tariff' | 'fee' | 'contractor'; row?: any } | null>(null);
  const [ratesFor, setRatesFor] = useState<any | null>(null);   // A1 access-fee editor
  const [crewsFor, setCrewsFor] = useState<any | null>(null);   // 053 crew presets (hourly tariffs)

  /**
   * A rep can only open General, so an admin-only tab in the URL resolves to it
   * rather than rendering an empty grid. Belt and braces with the isAdmin block
   * the cards already sit in: this decides what the BAR offers, that decides
   * what may render.
   */
  const effectiveTab: SettingsTab = isAdmin ? activeTab : 'general';
  const show = (t: SettingsTab) => effectiveTab === t;

  const company = companyArr?.[0] ?? {};
  const userName = (u: any) => `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || u.id?.slice(0, 6);

  const runGuard = useAction(); // one at a time: a second click is ignored
  const run = (fn: () => Promise<void>) => runGuard.run(() => runNow(fn));
  const runNow = async (fn: () => Promise<void>) => {
    try { await fn(); } catch (e: any) { window.alert(e?.message || 'Action failed'); }
  };

  // ---- Role assignments -------------------------------------------------
  const setRole = (u: any, role: string) => run(async () => { await api.patchUser(u.id, { role }); refetchUsers(); });
  const setDraw = (u: any, can_draw: boolean) => run(async () => { await api.patchUser(u.id, { can_draw }); refetchUsers(); });

  // ---- Branches / Tariffs / Fees / Contractors --------------------------
  const saveBranch = (data: any, id?: string) => run(async () => { id ? await api.updateBranch(id, data) : await api.createBranch(data); setModal(null); refetchBranches(); });
  const delBranch = (id: string) => run(async () => { if (window.confirm('Delete this branch?')) { await api.deleteBranch(id); refetchBranches(); } });
  const saveTariff = (data: any, id?: string) => run(async () => { id ? await api.updateTariff(id, data) : await api.createTariff(data); setModal(null); refetchTariffs(); });
  const delTariff = (id: string) => run(async () => { if (window.confirm('Delete this tariff?')) { await api.deleteTariff(id); refetchTariffs(); } });
  const saveFee = (data: any, id?: string) => run(async () => { id ? await api.updateFee(id, data) : await api.createFee(data); setModal(null); refetchFees(); });
  const delFee = (id: string) => run(async () => { if (window.confirm('Delete this fee?')) { await api.deleteFee(id); refetchFees(); } });
  const saveContractor = (data: any, id?: string) => run(async () => { id ? await api.updateContractor(id, data) : await api.createContractor(data); setModal(null); refetchContractors(); });
  const toggleContractor = (k: any) => run(async () => { await api.updateContractor(k.id, { active: !k.active }); refetchContractors(); });
  const delContractor = (id: string) => run(async () => { if (window.confirm('Delete this contractor?')) { await api.deleteContractor(id); refetchContractors(); } });

  const feeCustomer = (f: any) =>
    f.fee_type === 'pct' ? `${num(f.percentage)}% of ${f.applied_to === 'subtotal' ? 'subtotal' : 'base'}`
    : f.fee_type === 'unit' ? `${money(f.rate)}/${f.unit_label || 'unit'}`
    : money(f.amount);
  const feeContractor = (f: any) => `${feeContractorDefault(f)}${f.contractor_share_pct != null ? ` · local ${num(f.contractor_share_pct)}%` : ''}`;
  const feeContractorDefault = (f: any) => {
    if (f.fee_type === 'pct') return f.contractor_percentage != null ? `contractor: ${num(f.contractor_percentage)}%` : 'contractor: —';
    if (f.fee_type === 'unit') return f.contractor_rate != null ? `contractor: ${money(f.contractor_rate)}/${f.unit_label || 'unit'}` : 'contractor: —';
    return f.contractor_amount != null ? `contractor: ${money(f.contractor_amount)}` : 'contractor: —';
  };

  const feeGroup = (label: string, cat: string) => {
    const list = (fees ?? []).filter((f) => (cat === 'materials' ? f.category === 'materials' : f.category !== 'materials'));
    return (
      <div style={{ marginBottom: '10px' }}>
        <div style={{ fontSize: '10px', color: T.dim, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '4px 0 8px' }}>{label}</div>
        {list.length === 0 && empty('None yet.')}
        {list.map((f) => (
          <div key={f.id} style={rowStyle}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '12px', color: T.text }}>{f.name}</div>
              <div style={{ fontSize: '11px', color: T.muted, fontFamily: T.mono }}>{feeCustomer(f)} · <span style={{ color: f.fee_type === 'pct' ? (f.contractor_percentage != null ? T.green : T.dim) : (f[f.fee_type === 'unit' ? 'contractor_rate' : 'contractor_amount'] != null ? T.green : T.dim) }}>{feeContractor(f)}</span></div>
            </div>
            <button style={iconBtn} onClick={() => setModal({ kind: 'fee', row: f })} title="Edit"><Icons.edit size={15} aria-hidden="true" /></button>
            <button style={{ ...iconBtn, color: T.red }} onClick={() => delFee(f.id)} title="Delete"><Icons.delete size={15} aria-hidden="true" /></button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
      <h1 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '12px' }}>Settings</h1>

      {phone && user && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', padding: '10px 12px', background: T.panel, border: `1px solid ${T.border}`, borderRadius: '10px' }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: '13px', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.first_name} {user.last_name} <span style={{ color: T.muted }}>({user.role})</span>
          </div>
          <button onClick={logout} style={{ padding: '0 14px', background: T.panel2, border: `1px solid ${T.border}`, color: T.muted, borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>Log out</button>
        </div>
      )}

      {/* Hidden entirely for a rep: six tabs they cannot open is worse than no
          tab bar, and General is the only one with anything on it for them. */}
      {isAdmin && (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '16px', borderBottom: `1px solid ${T.border}`, paddingBottom: '10px' }}>
          {SETTINGS_TABS.map((t) => {
            const on = effectiveTab === t;
            return (
              <button
                key={t}
                onClick={() => onTabChange?.(t)}
                aria-current={on ? 'page' : undefined}
                style={{
                  padding: '7px 13px', borderRadius: '7px', fontSize: '12px', cursor: 'pointer',
                  fontWeight: on ? 700 : 500,
                  background: on ? T.accentFill : T.panel2,
                  color: on ? 'var(--crm-on-fill)' : T.muted,
                  border: `1px solid ${on ? T.accentLine : T.border}`,
                }}
              >
                {SETTINGS_TAB_LABEL[t]}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '14px', alignItems: 'start' }}>

        {/* 1. My Account (personal — every user) */}
        {show('general') && (
          <MyAccountCard />
        )}

        {/* 2. Email templates (G1c). Outside the admin block on purpose: the
            SHARED half is admin-managed, but every rep manages their own here,
            and a rep who cannot reach the page cannot reach their templates. */}
        {show('general') && (
          <EmailTemplatesCard />
        )}

        {/* 2b. Text templates (070). Beside email templates, for the same reason:
            everyone sees the list (what the Text box offers); only an admin
            edits it (also server-guarded). */}
        {show('general') && ringCentralEnabled && (
          <TextTemplatesCard />
        )}

        {/* ---- Company settings: ADMIN ONLY (also server-guarded) ---- */}
        {isAdmin && (<>

        {/* 2. Sales reps */}
        {show('users') && (
          <Card Icon={Icons.reps} title="Sales Reps">
            {(users ?? []).filter((u) => u.role === 'rep').length === 0 && empty('No reps yet.')}
            {(users ?? []).filter((u) => u.role === 'rep').map((u) => (
              <div key={u.id} style={rowStyle}><span style={{ flex: 1, fontSize: '12px' }}>{userName(u)}</span></div>
            ))}
            <div style={{ fontSize: '11px', color: T.dim, marginTop: '4px' }}>Reps are invited through Supabase auth; assign roles below.</div>
          </Card>
        )}

        {/* 3. Role assignments */}
        {show('users') && (
          <Card Icon={Icons.key} title="Role Assignments">
            {(users ?? []).length === 0 && empty('No users.')}
            {(users ?? []).map((u) => (
              <div key={u.id} style={rowStyle}>
                <span style={{ flex: 1, fontSize: '12px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName(u)}{u.id === user?.id && <span style={{ color: T.dim }}> (you)</span>}</span>
                <select value={u.role} onChange={(e) => setRole(u, e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
                  <option value="admin">Admin</option>
                  <option value="rep">Rep</option>
                </select>
                <select value={u.can_draw ? 'yes' : 'no'} onChange={(e) => setDraw(u, e.target.value === 'yes')} style={{ ...inputStyle, width: 'auto' }}>
                  <option value="yes">Can draw</option>
                  <option value="no">No draw</option>
                </select>
              </div>
            ))}
          </Card>
        )}

        {/* 4-7. config_lists cards */}
        {show('leads') && (
          <SimpleListCard Icon={Icons.inbox} title="Lead source options" listKey="lead_source" />
        )}
        {show('leads') && (
          <SimpleListCard Icon={Icons.clock} title="Arrival Windows" listKey="arrival_window" />
        )}
        {show('pricing') && (
          <SimpleListCard Icon={Icons.card} title="Payment Methods (we pay out)" listKey="payment_method" />
        )}
        {/* Two lists, because they answer different questions. See the note on
            DEFAULTS in routes/config-lists.ts: the client list must not offer
            cash or cheques, which both contracts refuse in writing, and the
            payout list keeps cash because some line hauls settle that way. */}
        {show('pricing') && (
          <SimpleListCard Icon={Icons.card} title="Payment Methods (clients pay us)" listKey="client_payment_method" />
        )}
        {show('pricing') && (
          <SimpleListCard Icon={Icons.tag} title="Expense Categories" listKey="expense_category" />
        )}

        {/* E4b: the terms printed on both contractor portals. Editable here so
            rewording them never needs a deploy. */}
        {show('contractors') && (
          <SimpleListCard Icon={Icons.document} title="Contractor Terms" listKey="contractor_terms" multiline full />
        )}

        {/* 8. Branches & tax rates */}
        {show('pricing') && (
          <Card Icon={Icons.building} title="Branches & Tax Rates" onAdd={() => setModal({ kind: 'branch' })}>
            {(branches ?? []).length === 0 && empty('No branches yet.')}
            {(branches ?? []).map((b) => (
              <div key={b.id} style={rowStyle}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: T.text }}>{b.name}</div>
                  <div style={{ fontSize: '11px', color: T.muted, fontFamily: T.mono }}>{b.province_state || '—'} · {num(b.tax_rate).toFixed(2)}%</div>
                </div>
                <button style={iconBtn} onClick={() => setModal({ kind: 'branch', row: b })} title="Edit"><Icons.edit size={15} aria-hidden="true" /></button>
                <button style={{ ...iconBtn, color: T.red }} onClick={() => delBranch(b.id)} title="Delete"><Icons.delete size={15} aria-hidden="true" /></button>
              </div>
            ))}
          </Card>
        )}

        {/* 9. Contractors (full width) */}
        {show('contractors') && (
          <Card Icon={Icons.truck} title="Contractors" onAdd={() => setModal({ kind: 'contractor' })} full>
            {(contractors ?? []).length === 0 && empty('No contractors yet.')}
            {(contractors ?? []).map((k) => (
              <div key={k.id} style={rowStyle}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', color: T.text }}>
                    {k.name}
                    <span style={{ fontSize: '9px', marginLeft: '8px', padding: '1px 7px', borderRadius: '4px', background: k.active ? `${T.greenTint}` : `${T.dim}`, color: k.active ? T.green : T.dim }}>{k.active ? 'Active' : 'Inactive'}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: T.muted }}>{[k.contact_name, k.email, formatPhone(k.phone)].filter(Boolean).join(' · ') || 'No contact on file'}</div>
                </div>
                <button style={iconBtn} onClick={() => setModal({ kind: 'contractor', row: k })} title="Edit"><Icons.edit size={15} aria-hidden="true" /></button>
                <button style={iconBtn} onClick={() => toggleContractor(k)} title={k.active ? 'Mark inactive' : 'Mark active'}>{k.active ? <Icons.pause size={15} /> : <Icons.play size={15} />}</button>
                <button style={{ ...iconBtn, color: T.red }} onClick={() => delContractor(k.id)} title="Delete"><Icons.delete size={15} aria-hidden="true" /></button>
              </div>
            ))}
          </Card>
        )}

        {/* 10. Tariffs (full width) */}
        {show('pricing') && (
          <Card Icon={Icons.pricing} title="Tariffs (Pricing Profiles)" onAdd={() => setModal({ kind: 'tariff' })} full>
            {(tariffs ?? []).length === 0 && empty('No tariffs yet.')}
            {(tariffs ?? []).map((t) => (
              <div key={t.id} style={rowStyle}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: T.text }}>{t.name}</div>
                  <div style={{ fontSize: '11px', color: T.muted, fontFamily: T.mono }}>{money(t.base_rate)} {t.pricing_method || '—'}{t.pricing_method === 'hourly' ? ` · deposit ${money(t.deposit_amount ?? 150)} · contractor labour ${num(t.contractor_labour_pct ?? 70)}%` : ` · min ${money(t.minimum_charge)}`}</div>
                  {/* 060: the fees a new estimate on this tariff starts with. */}
                  <div style={{ fontSize: '11px', color: T.dim }}>
                    Default fees: {((t.default_fee_ids ?? []) as string[]).map((id) => (fees ?? []).find((f) => f.id === id)).filter(Boolean).map((f: any) => `${f.name} ${feeValue(f)}`).join(' · ') || 'none'}
                  </div>
                </div>
                {/* 053: an hourly tariff's crews and their rates. */}
                {t.pricing_method === 'hourly' && (
                  <button
                    onClick={() => setCrewsFor(t)}
                    style={{ background: 'none', border: `1px solid ${T.border}`, color: T.muted, borderRadius: 6, padding: '3px 9px', fontSize: '11px', cursor: 'pointer', marginRight: '4px' }}
                    title="Configure crew presets for this tariff"
                  >
                    Crew presets{Array.isArray(t.crew_presets) && t.crew_presets.length ? ` (${t.crew_presets.length})` : ''}
                  </button>
                )}
                {/* A1: access fees belong to the tariff — the same elevator costs
                    different money under a different tariff. */}
                <button
                  onClick={() => setRatesFor(t)}
                  style={{ background: 'none', border: `1px solid ${T.border}`, color: T.muted, borderRadius: 6, padding: '3px 9px', fontSize: '11px', cursor: 'pointer', marginRight: '4px' }}
                  title="Configure access fees for this tariff"
                >
                  Access fees
                </button>
                <button style={iconBtn} onClick={() => setModal({ kind: 'tariff', row: t })} title="Edit"><Icons.edit size={15} aria-hidden="true" /></button>
                <button style={{ ...iconBtn, color: T.red }} onClick={() => delTariff(t.id)} title="Delete"><Icons.delete size={15} aria-hidden="true" /></button>
              </div>
            ))}
          </Card>
        )}

        {/* 11. Fee definitions (full width) */}
        {show('pricing') && (
          <Card Icon={Icons.money} title="Fee Definitions" onAdd={() => setModal({ kind: 'fee' })} full>
            {feeGroup('Additional Fees', 'additional')}
            {feeGroup('Materials', 'materials')}
          </Card>
        )}

        {/* 11b. Document templates (full width) */}
        {show('documents') && (
          <DocumentTemplatesCard />
        )}

        {/* "What's included" on the long-distance estimate portal and PDF
            (one bullet per row, under the heading "What's included"). Local moves show no block. */}
        {show('documents') && (
          <SimpleListCard Icon={Icons.document} title="Estimate: What's included (long distance)" listKey="estimate_included_ld" multiline full />
        )}

        {/* 11c. Declared Value Protection rates */}
        {/* L1: where every lead provider is handed a URL, a key and instructions. */}
        {show('leads') && (
          <LeadSourcesCard />
        )}

        {/* 073: the post-move feedback funnel. */}
        {show('leads') && <FeedbackSettingsCard />}

        {/* 045: what clients and reps searched the inventory for and could not find */}
        {show('leads') && (
          <InventorySearchMissesCard isAdmin={isAdmin} />
        )}

        {show('general') && (
          <EmailSignatureCard company={company} onSave={(patch) => run(async () => { if (company.id) { await api.updateCompany(company.id, patch); refetchCompany(); } })} />
        )}

        {show('pricing') && <PartnerSplitCard />}

        {show('pricing') && <SalesCommissionCard />}

        {show('pricing') && (
          <DvpRatesCard company={company} onSave={(patch) => run(async () => { if (company.id) { await api.updateCompany(company.id, patch); refetchCompany(); } })} />
        )}

        {show('pricing') && (
          <SquareFeesCard key={company.id ?? 'loading'} company={company} onSave={(patch) => run(async () => { if (company.id) { await api.updateCompany(company.id, patch); refetchCompany(); } })} />
        )}

        {/* 11d. Inventory media: what it costs, and what is holding it */}
        {/* Email sync (G1a) — per-mailbox Gmail status, and the switch per mailbox. */}
        {show('integrations') && (
          <EmailSyncCard />
        )}

        {/* RingCentral text sync (plan step 6): mode, per-store state, subscription. */}
        {show('integrations') && ringCentralEnabled && (
          <RingCentralCard />
        )}

        {show('integrations') && (
          <InventoryMediaCard company={company} onSave={(patch) => run(async () => { if (company.id) { await api.updateCompany(company.id, patch); refetchCompany(); } })} />
        )}

        {/* 12. Company info */}
        {show('general') && (
          <CompanyCard company={company} onSave={(patch) => run(async () => { if (company.id) { await api.updateCompany(company.id, patch); refetchCompany(); } })} />
        )}

        </>)}
      </div>

      {modal?.kind === 'branch' && <BranchModal row={modal.row} onSave={saveBranch} onClose={() => setModal(null)} />}
      {modal?.kind === 'tariff' && <TariffModal row={modal.row} fees={fees ?? []} onSave={saveTariff} onClose={() => setModal(null)} />}
      {ratesFor && <AccessorialRatesEditor tariff={ratesFor} onClose={() => setRatesFor(null)} />}
      {crewsFor && <CrewPresetsEditor tariff={crewsFor} onClose={() => setCrewsFor(null)} onSaved={() => { refetchTariffs(); }} />}
      {modal?.kind === 'fee' && <FeeModal row={modal.row} onSave={saveFee} onClose={() => setModal(null)} />}
      {modal?.kind === 'contractor' && <ContractorModal row={modal.row} onSave={saveContractor} onClose={() => setModal(null)} />}
    </div>
  );
}

// ---- My Account (personal — every user) ------------------------------------

function MyAccountCard() {
  const { user, updateUser } = useAuth();
  const api = useApiClient();
  const [first, setFirst] = useState(user?.first_name ?? '');
  const [last, setLast] = useState(user?.last_name ?? '');
  const [title, setTitle] = useState((user as any)?.title ?? '');
  const [phone, setPhone] = useState((user as any)?.phone ?? '');
  // Bumped after a save so the preview shows what the next email will carry.
  const [sigVersion, setSigVersion] = useState(0);
  const { data: signature } = useApi<any>(async () => (await api.getSignaturePreview()).data, { dependencies: [sigVersion] });
  const tz = userTz(user);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  const saveName = async () => {
    if (!user) return;
    if (first.trim() === (user.first_name ?? '') && last.trim() === (user.last_name ?? '')) return;
    try {
      await api.updateUser(user.id, { first_name: first.trim(), last_name: last.trim() });
      updateUser({ first_name: first.trim(), last_name: last.trim() });
      setSigVersion((v) => v + 1);
    } catch (e: any) {
      window.alert(e?.message || 'Could not save your name');
    }
  };
  const saveTitle = async () => {
    if (!user) return;
    const t = title.trim();
    if (t === ((user as any).title ?? '')) return;
    try {
      await api.updateUser(user.id, { title: t || null });
      updateUser({ title: t || null } as any);
      setSigVersion((v) => v + 1);
    } catch (e: any) {
      window.alert(e?.message || 'Could not save your title');
    }
  };
  const savePhone = async () => {
    if (!user) return;
    const v = phone.trim();
    if (v === ((user as any).phone ?? '')) return;
    try {
      await api.updateUser(user.id, { phone: v || null });
      updateUser({ phone: v || null } as any);
      setSigVersion((v2) => v2 + 1);
    } catch (e: any) {
      window.alert(e?.message || 'Could not save your phone number');
    }
  };
  const changeTz = async (value: string) => {
    if (!user || value === tz) return;
    updateUser({ timezone: value }); // optimistic — re-renders times immediately
    try {
      await api.updateUser(user.id, { timezone: value });
    } catch (e: any) {
      window.alert(e?.message || 'Could not save timezone');
    }
  };
  const changePw = async () => {
    if (pw.length < 8) { window.alert('Password must be at least 8 characters.'); return; }
    if (pw !== pw2) { window.alert('The two passwords do not match.'); return; }
    setSavingPw(true);
    try {
      const { error } = await supabaseClient.auth.updateUser({ password: pw });
      if (error) throw error;
      setPw(''); setPw2('');
      window.alert('Password updated.');
    } catch (e: any) {
      window.alert(`Could not update password: ${e?.message || 'try again'}.`);
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <Card Icon={Icons.user} title="My Account">
      <div style={{ fontSize: '11px', color: T.muted, marginBottom: '8px' }}>
        {user?.email} · <span style={{ textTransform: 'capitalize', color: user?.role === 'admin' ? T.accent : T.muted }}>{user?.role}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
        <Field label="First name"><input style={inputStyle} value={first} onChange={(e) => setFirst(e.target.value)} onBlur={saveName} /></Field>
        <Field label="Last name"><input style={inputStyle} value={last} onChange={(e) => setLast(e.target.value)} onBlur={saveName} /></Field>
      </div>
      <Field label="Title (shown on new-lead emails, and the {{sender_title}} merge field)">
        <input style={inputStyle} value={title} placeholder="e.g. Sales Manager" onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle} />
      </Field>
      {/* G1c: the {{sender_phone}} merge field had nowhere to read from until
          migration 040c added this column. Left blank it stays highlighted as
          missing in a template rather than sending the client a gap. */}
      <Field label="Phone (the {{sender_phone}} merge field)">
        <input style={inputStyle} value={phone} placeholder="e.g. (416) 555-0134" onChange={(e) => setPhone(e.target.value)} onBlur={savePhone} />
      </Field>
      <Field label="Timezone">
        <select style={inputStyle} value={tz} onChange={(e) => changeTz(e.target.value)}>
          {TZ_OPTIONS.some((o) => o.value === tz) ? null : <option value={tz}>{tzCityLabel(tz)}</option>}
          {TZ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>

      {/* YOUR SIGNATURE, AS IT WILL GO OUT (G1d round). Rendered on white
          because that is the background a client's mail client will put behind
          it — previewing it on the app's dark panel would be a preview of
          something nobody ever sees. A line you have not filled in is simply
          absent here, which is exactly what the client gets. */}
      <div style={{ marginTop: '10px' }}>
        <div style={{ ...labelRow, marginBottom: '4px' }}>Email signature</div>
        <div
          style={{ background: 'var(--crm-white)', borderRadius: '7px', padding: '10px 12px', border: `1px solid ${T.border}` }}
          dangerouslySetInnerHTML={{ __html: signature?.html ?? '' }}
        />
        <div style={{ fontSize: '10.5px', color: T.dim, marginTop: '5px', lineHeight: 1.5 }}>
          Added to every email you send from a lead file — your Gmail signature is not used.
          {signature?.omitted?.title && ' Set a title above and it appears here.'}
          {signature?.omitted?.phone && ' Set a phone above and it appears here.'}
          {(signature?.omitted?.officePhone || signature?.omitted?.website) &&
            ' The office number and website are company settings an admin manages.'}
        </div>
      </div>
      <div style={{ marginTop: '10px', borderTop: `1px solid ${T.border}`, paddingTop: '10px' }}>
        <div style={{ fontSize: '10px', color: T.muted, marginBottom: '6px' }}>Change password</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px' }}>
          <input type="password" placeholder="New password" style={inputStyle} value={pw} onChange={(e) => setPw(e.target.value)} />
          <input type="password" placeholder="Confirm" style={inputStyle} value={pw2} onChange={(e) => setPw2(e.target.value)} />
          <button style={{ ...primaryBtn, opacity: savingPw ? 0.6 : 1 }} disabled={savingPw} onClick={changePw}>Update</button>
        </div>
      </div>
    </Card>
  );
}

// ---- Simple config_lists card ----------------------------------------------

// `multiline` (E4b): the contractor terms are long paragraphs, so that card
// wraps its rows and edits in a textarea. The short option lists keep the
// single-line ellipsis look.
function SimpleListCard({ Icon, title, listKey, multiline, full }: { Icon: React.ComponentType<any>; title: string; listKey: string; multiline?: boolean; full?: boolean }) {
  const api = useApiClient();
  const { data, refetch } = useApi<any[]>(async () => (await api.getConfigList(listKey)).data || [], { dependencies: [listKey] });
  const [val, setVal] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const addGuard = useAction(); // one at a time: a second click is ignored
  const add = () => addGuard.run(() => addNow());
  const addNow = async () => {
    if (!val.trim()) return;
    try { await api.addConfigListItem(listKey, val.trim()); setVal(''); refetch(); } catch (e: any) { window.alert(e?.message || 'Failed'); }
  };
  const del = async (id: string) => { try { await api.deleteConfigListItem(id); refetch(); } catch (e: any) { window.alert(e?.message || 'Failed'); } };

  const startEdit = (item: any) => { setEditingId(item.id); setDraft(item.value); };
  const cancelEdit = () => { setEditingId(null); setDraft(''); };
  const saveEdit = async () => {
    const next = draft.trim();
    if (!next || !editingId) return cancelEdit();
    setBusy(true);
    try { await api.updateConfigListItem(editingId, next); cancelEdit(); refetch(); }
    catch (e: any) { window.alert(e?.message || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Card Icon={Icon} title={title} full={full}>
      {(data ?? []).length === 0 && empty('None yet.')}
      {(data ?? []).map((item) =>
        editingId === item.id ? (
          <div key={item.id} style={{ ...rowStyle, alignItems: 'flex-start', gap: '6px' }}>
            {multiline ? (
              <textarea
                autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit(); }}
                rows={3} style={{ ...inputStyle, flex: 1, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }}
              />
            ) : (
              <input
                autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                style={{ ...inputStyle, flex: 1 }}
              />
            )}
            <button style={{ ...iconBtn, color: T.accent }} onClick={saveEdit} disabled={busy} title="Save"><Icons.check size={15} aria-hidden="true" /></button>
            <button style={iconBtn} onClick={cancelEdit} disabled={busy} title="Cancel"><Icons.close size={15} aria-hidden="true" /></button>
          </div>
        ) : (
          <div key={item.id} style={{ ...rowStyle, alignItems: multiline ? 'flex-start' : 'center' }}>
            <span style={{ flex: 1, fontSize: '12px', lineHeight: multiline ? 1.6 : undefined, whiteSpace: multiline ? 'normal' : undefined }}>{item.value}</span>
            <button style={iconBtn} onClick={() => startEdit(item)} title="Edit"><Icons.edit size={15} aria-hidden="true" /></button>
            <button style={{ ...iconBtn, color: T.red }} onClick={() => del(item.id)} title="Delete"><Icons.delete size={15} aria-hidden="true" /></button>
          </div>
        )
      )}
      <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
        <input style={inputStyle} placeholder={`Add ${title.toLowerCase().replace(/s$/, '')}…`} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
        <button style={plusBtn} onClick={add}>+</button>
      </div>
    </Card>
  );
}

// ---- Company card (commit-on-blur) -----------------------------------------

// ---- Document Templates card (Phase 8i.3) ----------------------------------

const MOVE_LABEL: Record<string, string> = { long_distance: 'Long Distance', local: 'Local', any: 'Both' };

function DocumentTemplatesCard() {
  const api = useApiClient();
  const { data: templates, refetch } = useApi<any[]>(async () => (await api.getDocumentTemplates()).data || []);
  const [editor, setEditor] = useState<{ template: any | null } | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [report, setReport] = useState<any | null>(null);

  /**
   * THE REFRESH, MADE DELIBERATE. It has always run lazily on any request that
   * lists templates — which is to say on opening a lead's Estimate tab or this
   * page — and reported nothing, so the only way to know whether new wording had
   * landed was to generate a document and read it. This runs the same function
   * and shows what it did to each template.
   */
  const refreshBuiltIns = async () => {
    setRefreshing(true); setReport(null);
    try {
      const res: any = await api.refreshBuiltInTemplates();
      setReport(res?.data ?? null);
      refetch();
    } catch (e: any) {
      // A failed RUN is its own outcome, distinct from a run in which rows
      // failed. Both must be visible; neither may look like success.
      setReport({ ok: false, error: e?.message || 'The refresh could not be run.', rows: [], counts: null });
    } finally {
      setRefreshing(false);
    }
  };

  const rows = [...(templates ?? [])].sort((a, b) => (a.name || '').localeCompare(b.name || '') || (a.move_type || '').localeCompare(b.move_type || ''));

  const del = async (t: any) => {
    if (!window.confirm(`Delete the template "${t.name} · ${MOVE_LABEL[t.move_type] ?? t.move_type}"?`)) return;
    try { await api.deleteDocumentTemplate(t.id); refetch(); } catch (e: any) { window.alert(e?.message || 'Failed to delete'); }
  };

  return (
    <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: T.text }}>
          <Icons.document size={16} aria-hidden="true" />Document Templates
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button
            onClick={refreshBuiltIns}
            disabled={refreshing}
            title="Update the built-in templates to the current shipped wording. Templates you have customized are never overwritten."
            style={{ padding: '5px 11px', background: T.panel2, color: T.text, border: `1px solid ${T.border}`, borderRadius: '6px', cursor: refreshing ? 'default' : 'pointer', fontSize: '11px', fontWeight: 600, opacity: refreshing ? 0.6 : 1 }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh built-in templates'}
          </button>
          {/* Advanced affordance — deliberately quiet (text link, not a primary button). */}
          <button onClick={() => setEditor({ template: null })} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: '11px', textDecoration: 'underline' }}>
            + New template
          </button>
        </div>
      </div>

      {report && <RefreshReport report={report} onDismiss={() => setReport(null)} />}

      {rows.length === 0 && <div style={{ fontSize: '12px', color: T.dim, padding: '4px 0' }}>No templates.</div>}
      {rows.map((t) => (
        <div key={t.id} style={rowStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: '12px', color: T.text }}>{t.name}</span>
            <span style={{ fontSize: '11px', color: T.muted }}> · {MOVE_LABEL[t.move_type] ?? t.move_type}</span>
            <span style={{ fontSize: '9px', marginLeft: '8px', padding: '1px 7px', borderRadius: '4px', background: t.is_built_in ? `${T.accentTint}` : `${T.amberTint}`, color: t.is_built_in ? T.accent : T.amber }}>
              {t.is_built_in ? 'Built-in' : 'Custom'}
            </span>
          </div>
          <button style={iconBtn} onClick={() => setEditor({ template: t })} title="Edit"><Icons.edit size={15} aria-hidden="true" /></button>
          {t.is_built_in ? (
            <button style={{ ...iconBtn, color: T.dim, cursor: 'not-allowed' }} disabled title="Built-in templates can't be deleted"><Icons.delete size={15} aria-hidden="true" /></button>
          ) : (
            <button style={{ ...iconBtn, color: T.red }} onClick={() => del(t)} title="Delete"><Icons.delete size={15} aria-hidden="true" /></button>
          )}
        </div>
      ))}

      {editor && (
        <TemplateEditor
          template={editor.template}
          existing={templates ?? []}
          onClose={() => setEditor(null)}
          onSaved={refetch}
        />
      )}
    </div>
  );
}

/**
 * What the refresh actually did, per template.
 *
 * FIVE OUTCOMES, NEVER FOUR. 'skipped' and 'failed' are different facts and are
 * shown differently: a template we deliberately left alone (because you have
 * customized it) is not a template we tried and could not update. Every skip and
 * every failure carries its reason inline — a count alone would leave you where
 * this whole feature started, knowing something happened but not what.
 *
 * "Nothing needed doing" is stated explicitly rather than shown as an empty
 * list, because an empty result and a result of nothing look identical and only
 * one of them means the refresh worked.
 */
function RefreshReport({ report, onDismiss }: { report: any; onDismiss: () => void }) {
  const rows: any[] = Array.isArray(report?.rows) ? report.rows : [];
  const c = report?.counts;

  const STYLE: Record<string, { fg: string; label: string }> = {
    refreshed:         { fg: T.green, label: 'UPDATED' },
    seeded:            { fg: T.green, label: 'CREATED' },
    'already-current': { fg: T.muted, label: 'CURRENT' },
    skipped:           { fg: T.amber, label: 'SKIPPED' },
    failed:            { fg: T.red, label: 'FAILED' },
  };

  // The run itself failed — no rows to show, and it must not read as "nothing
  // needed doing".
  if (report?.error) {
    return (
      <div style={{ border: `1px solid ${T.redSoftLine}`, background: T.panel2, borderRadius: '8px', padding: '11px 13px', marginBottom: '10px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Icons.warning size={14} color={T.red} aria-hidden="true" />
          <span style={{ fontSize: '12px', fontWeight: 700, color: T.text }}>The refresh did not run</span>
          <span style={{ flex: 1 }} />
          <button onClick={onDismiss} aria-label="Dismiss" style={iconBtn}><Icons.close size={14} aria-hidden="true" /></button>
        </div>
        <div style={{ fontSize: '12px', color: T.red, marginTop: '6px' }}>{report.error}</div>
      </div>
    );
  }

  const changed = (c?.refreshed ?? 0) + (c?.seeded ?? 0);
  const border = (c?.failed ?? 0) > 0 ? `${T.redTint}` : (c?.skipped ?? 0) > 0 ? `${T.amberTint}` : `${T.accentTint}`;

  return (
    <div style={{ border: `1px solid ${border}`, background: T.panel2, borderRadius: '8px', padding: '11px 13px', marginBottom: '10px' }}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, color: T.text }}>
          {changed > 0 ? `${changed} template${changed === 1 ? '' : 's'} updated` : 'No templates needed updating'}
        </span>
        <span style={{ fontSize: '11px', color: T.muted }}>
          {c?.alreadyCurrent ? `${c.alreadyCurrent} already current · ` : ''}
          {c?.skipped ? `${c.skipped} skipped · ` : ''}
          {c?.failed ? `${c.failed} failed · ` : ''}
          target {report?.targetVersion}
        </span>
        <span style={{ flex: 1 }} />
        <button onClick={onDismiss} aria-label="Dismiss" style={iconBtn}><Icons.close size={14} aria-hidden="true" /></button>
      </div>

      <div style={{ marginTop: '8px' }}>
        {rows.map((r, i) => {
          const st = STYLE[r.outcome] ?? STYLE.skipped;
          return (
            <div key={i} style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', padding: '3px 0', fontSize: '11.5px' }}>
              <span style={{ minWidth: '62px', fontSize: '9px', fontWeight: 700, letterSpacing: '0.4px', color: st.fg, paddingTop: '2px' }}>{st.label}</span>
              <span style={{ minWidth: '210px', color: T.text }}>
                {r.name} <span style={{ color: T.muted }}>· {MOVE_LABEL[r.moveType] ?? r.moveType}</span>
              </span>
              <span style={{ flex: 1, color: T.muted }}>
                {r.reason}
                {r.outcome === 'refreshed' && r.fromVersion ? ` (${r.fromVersion} → ${r.toVersion})` : ''}
                {r.chars ? ` · ${r.chars.toLocaleString()} chars` : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Declared Value Protection rates (migration 034).
 *
 * THESE ARE CONFIGURATION, NOT CONSTANTS, for the reason D1/A11 established for
 * the card-fee rate: a rate hardcoded in the application goes stale silently.
 * The contract keeps printing the old number, every party signs it, and nothing
 * anywhere reports a problem. Changing a figure here changes every contract
 * generated from that moment on, and changes none already issued — those carry
 * the rate they were generated with, baked into their own snapshot.
 *
 * Blank or non-numeric input is REFUSED rather than stored: migration 034's
 * check constraint would reject it anyway, and a rate that silently became zero
 * would quote every client a $0 premium.
 */
/**
 * The partner split, as an effective-dated schedule.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A LIST AND NOT TWO NUMBER FIELDS
 * ---------------------------------------------------------------------------
 * The split changed on 2026-07-01 from 60/40 to 55/45, and every month before
 * that date still has to split at the old rate. Two fields can only hold the
 * CURRENT split, so saving a new one would restate two years of closed books —
 * which is the entire reason migration 042 stores rows.
 *
 * SAVED AS A WHOLE, for the same reason the route replaces rather than patches:
 * a per-row save can leave two periods claiming the same start date, or remove
 * the earliest row and leave 2024 with no split at all. Here that shows up as
 * one Save button and inline arithmetic that refuses to let a period leave the
 * page not totalling 100.
 */
function PartnerSplitCard() {
  const api = useApiClient();
  const { data: loaded, refetch } = useApi<any[]>(async () => (await api.getPartnerSplits()).data || []);
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // The server's copy until the first edit, then the draft.
  const list: any[] = rows ?? (loaded ?? []);
  const sorted = [...list].sort((a, b) => String(a.effectiveFrom).localeCompare(String(b.effectiveFrom)));

  const edit = (i: number, patch: any) => {
    const next = [...sorted];
    next[i] = { ...next[i], ...patch };
    setRows(next);
    setMsg(null);
  };
  /** Adrian and Daryl are two halves of one number — typing either sets both. */
  const setAdrian = (i: number, v: string) => {
    const a = Number(v);
    edit(i, { adrianPct: v, darylPct: Number.isFinite(a) ? String(round2(100 - a)) : sorted[i].darylPct });
  };
  const setDaryl = (i: number, v: string) => {
    const d = Number(v);
    edit(i, { darylPct: v, adrianPct: Number.isFinite(d) ? String(round2(100 - d)) : sorted[i].adrianPct });
  };

  const totalOf = (r: any) => round2(num(r.adrianPct) + num(r.darylPct));
  const problems = sorted
    .map((r, i) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(r.effectiveFrom ?? ''))) return `Period ${i + 1} needs a start date.`;
      if (Math.abs(totalOf(r) - 100) >= 0.005) return `${r.effectiveFrom} adds up to ${totalOf(r)}%, not 100%.`;
      if (num(r.adrianPct) < 0 || num(r.darylPct) < 0) return `${r.effectiveFrom} has a negative share.`;
      return null;
    })
    .filter(Boolean) as string[];
  const dupes = sorted.filter((r, i) => i > 0 && r.effectiveFrom === sorted[i - 1].effectiveFrom);
  if (dupes.length) problems.push(`Two periods both start on ${dupes[0].effectiveFrom}.`);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.savePartnerSplits(sorted.map((r) => ({
        effectiveFrom: String(r.effectiveFrom).slice(0, 10),
        adrianPct: num(r.adrianPct),
        darylPct: num(r.darylPct),
        note: r.note ?? null,
      })));
      setRows(null);
      await refetch();
      setMsg({ ok: true, text: 'Saved. Every screen that splits money now uses this.' });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.message || 'Could not save the split.' });
    } finally {
      setBusy(false);
    }
  };

  const small: React.CSSProperties = { ...inputStyle, padding: '5px 7px', fontSize: '11px' };

  return (
    <Card Icon={Icons.money} title="Partner split" full>
      <div style={{ fontSize: '11px', color: T.muted, marginBottom: '10px' }}>
        Each row applies from its start date until the next one begins. Money is split at the rate in
        force on its own date, so changing the current split never restates an earlier month.
      </div>

      {sorted.length === 0 && empty('No split periods on file.')}
      {sorted.map((r, i) => (
        <div key={i} style={{ ...rowStyle, alignItems: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '9px', color: T.dim, textTransform: 'uppercase' }}>From</span>
            <input type="date" style={{ ...small, width: '140px' }} value={String(r.effectiveFrom ?? '').slice(0, 10)}
              onChange={(e) => edit(i, { effectiveFrom: e.target.value })} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '9px', color: T.dim, textTransform: 'uppercase' }}>Adrian %</span>
            <input {...numberInput(0.01)} style={{ ...small, width: '84px' }} value={String(r.adrianPct ?? '')}
              onChange={(e) => setAdrian(i, e.target.value)} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '9px', color: T.dim, textTransform: 'uppercase' }}>Daryl %</span>
            <input {...numberInput(0.01)} style={{ ...small, width: '84px' }} value={String(r.darylPct ?? '')}
              onChange={(e) => setDaryl(i, e.target.value)} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: '160px' }}>
            <span style={{ fontSize: '9px', color: T.dim, textTransform: 'uppercase' }}>Note</span>
            <input style={small} value={r.note ?? ''} onChange={(e) => edit(i, { note: e.target.value })} />
          </label>
          <span style={{ fontSize: '11px', fontFamily: T.mono, color: Math.abs(totalOf(r) - 100) < 0.005 ? T.green : T.red, paddingBottom: '6px' }}>
            {totalOf(r)}%
          </span>
          <button
            style={{ ...iconBtn, color: T.red, paddingBottom: '6px' }}
            title={sorted.length === 1 ? 'The schedule needs at least one period' : 'Remove this period'}
            disabled={sorted.length === 1}
            onClick={() => { setRows(sorted.filter((_, j) => j !== i)); setMsg(null); }}
          >
            <Icons.delete size={15} aria-hidden="true" />
          </button>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
        <button
          style={ghostBtn}
          onClick={() => { setRows([...sorted, { effectiveFrom: '', adrianPct: '50', darylPct: '50', note: '' }]); setMsg(null); }}
        >
          + Add a period
        </button>
        <button style={{ ...primaryBtn, opacity: busy || problems.length ? 0.5 : 1 }} disabled={busy || problems.length > 0} onClick={save}>
          {busy ? 'Saving…' : 'Save schedule'}
        </button>
        {rows && <button style={ghostBtn} onClick={() => { setRows(null); setMsg(null); }}>Discard changes</button>}
      </div>

      {problems.map((p) => (
        <div key={p} style={{ fontSize: '11px', color: T.red, marginTop: '6px' }}>{p}</div>
      ))}
      {msg && <div style={{ fontSize: '11px', color: msg.ok ? T.green : T.red, marginTop: '6px' }}>{msg.text}</div>}
    </Card>
  );
}

function DvpRatesCard({ company, onSave }: { company: any; onSave: (patch: any) => void }) {
  const fields: { key: string; label: string; suffix: string; fallback: number }[] = [
    { key: 'dvp_min_rate_per_lb', label: 'Minimum value', suffix: '$ per lb of estimated weight', fallback: 6.0 },
    { key: 'dvp_min_declared_value', label: 'Value floor', suffix: '$ minimum declared value', fallback: 6000 },
    { key: 'dvp_premium_per_1000', label: 'Premium', suffix: '$ per $1,000 declared (pro-rata)', fallback: 18.0 },
    { key: 'dvp_min_premium', label: 'Minimum premium', suffix: '$ per contract', fallback: 450 },
    { key: 'dvp_deductible', label: 'Deductible', suffix: '$ per claim', fallback: 500 },
  ];
  const commit = (key: string, raw: string, fallback: number) => {
    const n = Number(raw);
    const current = Number(company[key] ?? fallback);
    // >= 0 for the deductible, > 0 for the three rates — mirrors the constraint.
    // A zero deductible and a zero minimum premium are both real settings — the
    // latter means "no minimum", which is how this priced before migration 035.
    // The three rates are different: a zero there quotes every client $0.
    const floor = key === 'dvp_deductible' || key === 'dvp_min_premium' ? 0 : Number.MIN_VALUE;
    if (!Number.isFinite(n) || n < floor) { window.alert(`${key.replace(/_/g, ' ')} must be a number${floor > 0 ? ' greater than zero' : ' of zero or more'}.`); return; }
    if (n !== current) onSave({ [key]: n });
  };
  return (
    <Card Icon={Icons.signature} title="Declared Value Protection">
      <div style={{ fontSize: '11px', color: T.muted, marginBottom: '10px', lineHeight: 1.5 }}>
        Long distance only. Local moves carry Enhanced Coverage at $0.60/lb and are never offered this option.
        The premium is the pro-rata figure or the minimum, whichever is higher.
        Changing a rate affects contracts generated from now on; documents already issued keep the rate they were generated with.
      </div>
      {fields.map((f) => (
        <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <label style={{ fontSize: '11px', color: T.muted, width: '110px', flexShrink: 0 }}>{f.label}</label>
          <input
            style={{ ...inputStyle, width: '96px', fontFamily: T.mono }}
            {...numberInput(f.key === 'dvp_min_rate_per_lb' ? STEP_PER_LB : 1)}
            defaultValue={String(company[f.key] ?? f.fallback)}
            onBlur={(e) => commit(f.key, e.target.value, f.fallback)}
          />
          <span style={{ fontSize: '11px', color: T.dim }}>{f.suffix}</span>
        </div>
      ))}
      {!company.id && <div style={{ fontSize: '11px', color: T.dim, marginTop: '2px' }}>Loading company…</div>}
    </Card>
  );
}

/**
 * SALES COMMISSION (Finalize phase 2, 049) — each user's standard %.
 *
 * Only the job's sales rep (assigned_rep_id) earns, on the invoice subtotal after
 * adjustments, before tax, tip and CC fee, less the DVP premium. An admin can
 * override the rate per job on the Finalize tab. Every rate starts at 0 %.
 *
 * Its own admin-only table and route (/api/finalize-admin/commission-rates) —
 * never a column on users, which every signed-in user can read. A rate change
 * applies to invoices issued afterwards; an issued commission keeps its %.
 */
function SalesCommissionCard() {
  const api = useApiClient();
  const { data: rows, refresh } = useApi<any[]>(async () => (await api.getCommissionRates()).data || []);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const commit = async (r: any, raw: string) => {
    const n = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(n) || n < 0 || n > 100) { setMsg({ ok: false, text: `${r.name}: enter a percentage from 0 to 100.` }); return; }
    if (n === Number(r.pct)) return;
    try {
      await api.setCommissionRate(r.userId, n);
      setMsg({ ok: true, text: `${r.name}: ${n}% saved.` });
      refresh().catch(() => {});
    } catch (e: any) { setMsg({ ok: false, text: e?.message || 'Could not save the rate.' }); }
  };
  return (
    <Card Icon={Icons.pricing} title="Sales commission">
      <div style={{ fontSize: '11px', color: T.muted, marginBottom: '10px', lineHeight: 1.5 }}>
        Each user's standard rate when they are the job's sales rep. Paid on the invoice subtotal after adjustments —
        before tax, tip and the card fee, and excluding the DVP premium. Override per job on the Finalize tab.
        A change applies to invoices issued from now on.
      </div>
      {!rows && <div style={{ fontSize: '11px', color: T.dim }}>Loading…</div>}
      {(rows ?? []).map((r) => (
        <div key={r.userId} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <label style={{ fontSize: '12px', color: T.text, width: '160px', flexShrink: 0 }}>
            {r.name} <span style={{ fontSize: '10px', color: T.dim }}>{r.role}</span>
          </label>
          <input
            key={`${r.userId}:${r.pct}`}
            style={{ ...inputStyle, width: '80px', fontFamily: T.mono }}
            {...numberInput(0.01)} min={0} max={100}
            defaultValue={String(r.pct)}
            onBlur={(e) => commit(r, e.target.value)}
          />
          <span style={{ fontSize: '11px', color: T.dim }}>%</span>
        </div>
      ))}
      {msg && <div style={{ fontSize: '11px', color: msg.ok ? T.green : T.red, marginTop: '4px' }}>{msg.text}</div>}
    </Card>
  );
}

/**
 * SQUARE'S FEE (048) — the prefill for Square's cost on a card payment:
 * charged × rate % + fixed. Only a starting point; the figure entered on the
 * payment (from Square's report) is what is booked as a Processor Fee expense.
 * Not the CC fee charged to clients — that is cc_fee_rate.
 */
function SquareFeesCard({ company, onSave }: { company: any; onSave: (patch: any) => void }) {
  const fields: { key: string; label: string; suffix: string; fallback: number }[] = [
    { key: 'square_fee_rate', label: 'Rate', suffix: '% of the charged total', fallback: 2.9 },
    { key: 'square_fee_fixed', label: 'Fixed', suffix: '$ per card payment', fallback: 0.3 },
  ];
  const commit = (key: string, raw: string, fallback: number) => {
    const n = Number(raw);
    const current = Number(company[key] ?? fallback);
    if (raw.trim() === '' || !Number.isFinite(n) || n < 0) { window.alert(`${key.replace(/_/g, ' ')} must be a number of zero or more.`); return; }
    if (n !== current) onSave({ [key]: n });
  };
  return (
    <Card Icon={Icons.pricing} title="Square processing cost">
      <div style={{ fontSize: '11px', color: T.muted, marginBottom: '10px', lineHeight: 1.5 }}>
        Prefills Square's cost when a card payment is recorded: charged total × rate + fixed.
        The rep can change it to what Square actually charged; that figure is booked as a Processor Fee expense.
        This is not the card fee charged to clients.
      </div>
      {fields.map((f) => (
        <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <label style={{ fontSize: '11px', color: T.muted, width: '110px', flexShrink: 0 }}>{f.label}</label>
          <input
            style={{ ...inputStyle, width: '96px', fontFamily: T.mono }}
            {...numberInput(0.01)}
            defaultValue={String(company[f.key] ?? f.fallback)}
            onBlur={(e) => commit(f.key, e.target.value, f.fallback)}
          />
          <span style={{ fontSize: '11px', color: T.dim }}>{f.suffix}</span>
        </div>
      ))}
      {!company.id && <div style={{ fontSize: '11px', color: T.dim, marginTop: '2px' }}>Loading company…</div>}
    </Card>
  );
}

/**
 * EMAIL SIGNATURE — the company half (G1d round).
 *
 * Every CRM email is signed from two places: the sender's own name, title and
 * phone (each person sets those in My Account) and these company details, which
 * are the same for everyone and therefore belong to an admin.
 *
 * THEY ARE THE SAME COLUMNS AS COMPANY INFO, not a second copy. Editing the
 * office number here changes it there, and vice versa — there is one office
 * number. They are surfaced here as well because "why is the website missing
 * from my signature" is a question asked while looking at a signature, and
 * sending someone to a different card to answer it is how a setting stays wrong.
 *
 * THE LOGO IS NOT EDITABLE HERE, and that is honest rather than lazy: it ships
 * with the app as a file, and pretending otherwise would put an upload control
 * on screen that writes nowhere.
 */
function EmailSignatureCard({ company, onSave }: { company: any; onSave: (patch: any) => void }) {
  const api = useApiClient();
  const { data: signature, refetch } = useApi<any>(async () => (await api.getSignaturePreview()).data);

  const field = (key: string, label: string, hint: string) => (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ ...labelRow, marginBottom: '3px' }}>{label}</div>
      <input
        style={inputStyle}
        defaultValue={company[key] ?? ''}
        onBlur={(e) => {
          if (e.target.value !== (company[key] ?? '')) {
            onSave({ [key]: e.target.value });
            // The preview reads the saved row, so it refreshes a beat behind the
            // write rather than optimistically showing something unsaved.
            window.setTimeout(refetch, 400);
          }
        }}
      />
      <div style={{ fontSize: '10.5px', color: T.dim, marginTop: '3px' }}>{hint}</div>
    </div>
  );

  return (
    <Card Icon={Icons.signature} title="Email signature">
      <div style={{ fontSize: '11px', color: T.dim, marginBottom: '10px', lineHeight: 1.5 }}>
        These appear in everyone's signature. Each person's own name, title and phone are set
        in their My Account card.
      </div>

      {field('phone', 'Office phone', 'Shown as "Office:" under the sender’s own number.')}
      {field('web', 'Website', 'Linked. Written without the https:// prefix.')}

      <div style={{ ...labelRow, margin: '10px 0 4px' }}>Logo</div>
      <div style={{ background: 'var(--crm-white)', borderRadius: '7px', padding: '10px', border: `1px solid ${T.border}`, display: 'inline-block' }}>
        <img src="/brand/logo-email-header.png" alt="Company logo" style={{ height: '44px', width: 'auto', display: 'block' }} />
      </div>
      <div style={{ fontSize: '10.5px', color: T.dim, marginTop: '5px', lineHeight: 1.5 }}>
        Sent as a PNG from a public URL rather than embedded. Email clients cannot display the SVG
        the app uses elsewhere, and Outlook on Windows would show a broken box. Where a client blocks
        images, this degrades to the company name as alt text.
      </div>

      <div style={{ ...labelRow, margin: '12px 0 4px' }}>Preview (yours)</div>
      <div
        style={{ background: 'var(--crm-white)', borderRadius: '7px', padding: '10px 12px', border: `1px solid ${T.border}` }}
        dangerouslySetInnerHTML={{ __html: signature?.html ?? '' }}
      />
    </Card>
  );
}

/**
 * EMAIL TEMPLATES (G1c) — shared, and mine.
 *
 * ONE CARD, TWO HALVES, because they are the same object with one column
 * different and two cards would invite the question of which one a template is
 * "really" in. Shared is the company talking and only an admin changes it; Mine
 * belongs to whoever is looking and nobody else ever sees it — not even an
 * admin, who can run the company without reading a colleague's drafts.
 *
 * A REP SEES SHARED TEMPLATES READ-ONLY. They are not hidden: a rep needs to
 * know what the company's wording is, and the escape hatch for wanting it
 * different is "Save as my template" in the compose box, which needs no meeting.
 */
/**
 * TEXT TEMPLATES (migration 070) — what the Sales page Text box offers.
 *
 * Company-wide: everyone sees the list; an admin creates, edits, deletes and
 * reorders (the server checks too). Plain text with the same {{merge fields}}
 * as email — only the ones that make sense in a text. The editor shows the
 * character count (RingCentral's limit is 1000) and a preview filled with a
 * made-up long-distance lead, so the LD rule — a pickup window, not a date —
 * is visible while writing.
 */
/**
 * The automatic first text on new intake leads (071, lib/autoText.ts): the
 * switch, on / off per lead source, and which template each source sends
 * (072 — default "First contact — new lead"). Admins change it; everyone sees it.
 */
const FIRST_CONTACT_TEMPLATE = 'First contact — new lead';
function AutoTextSettings({ templates }: { templates: any[] }) {
  const api = useApiClient();
  const { data, error, refetch } = useApi<any>(async () => (await api.getAutoTextSettings()).data);
  const [busy, setBusy] = useState(false);
  const canManage = !!data?.canManage;
  const change = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); refetch(); }
    catch (e: any) { window.alert(e?.message || 'Could not save that.'); }
    finally { setBusy(false); }
  };
  if (error) return <div style={{ fontSize: '11px', color: T.amber, marginBottom: '10px' }}>Auto-text settings could not be loaded: {String(error)}</div>;
  if (!data) return null;
  const sources: any[] = data.sources ?? [];
  return (
    <div style={{ marginBottom: '14px', paddingBottom: '12px', borderBottom: `1px solid ${T.border}` }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 600, color: T.text, cursor: canManage ? 'pointer' : 'default' }}>
        <input type="checkbox" checked={!!data.enabled} disabled={!canManage || busy}
               onChange={(e) => change(() => api.setAutoTextEnabled(e.target.checked))} />
        Send first-contact text on new leads
      </label>
      <div style={{ fontSize: '11px', color: T.dim, margin: '4px 0 8px 24px', lineHeight: 1.5 }}>
        Sends the lead source's template (normally "First contact — new lead") from the main number when a lead comes in through intake. Only to a valid Canadian
        number that has not opted out, is not ignored, and has no other lead or text in the last 30 days; never with a [no …] gap. It goes the
        moment the lead comes in, any time of day. Leads typed in with New Lead never get one.
      </div>
      {data.enabled && (
        <div style={{ marginLeft: '24px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: T.muted, marginBottom: '4px' }}>Lead sources</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {sources.map((src) => (
              <div key={src.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', minWidth: '190px', color: src.active ? T.text : T.dim, cursor: canManage ? 'pointer' : 'default' }}>
                  <input type="checkbox" checked={!!src.auto_text} disabled={!canManage || busy}
                         onChange={(e) => change(() => api.setSourceAutoText(src.id, e.target.checked))} />
                  {src.name}{!src.active && ' (inactive)'}
                </label>
                {data.perSourceTemplates && src.auto_text && (
                  <select value={src.template_id ?? ''} disabled={!canManage || busy}
                          aria-label={`Text template for ${src.name}`}
                          onChange={(e) => change(() => api.setSourceAutoTextTemplate(src.id, e.target.value || null))}
                          style={{ fontSize: '11px', padding: '3px 6px', borderRadius: '5px', maxWidth: '100%', background: T.panel2, color: T.text, border: `1px solid ${T.border}` }}>
                    <option value="">{FIRST_CONTACT_TEMPLATE} (default)</option>
                    {templates.filter((t) => t.name !== FIRST_CONTACT_TEMPLATE).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Settings → Feedback (073, lib/feedback.ts): the post-move feedback request —
 * sent at 10:00 the day after a job is moved to Closed, one reminder email a
 * week later. The master switch, email / text, and the review sites a 4–5 star
 * client is offered (a site with no link is not shown). Admins change it.
 */
function FeedbackSettingsCard() {
  const api = useApiClient();
  const { data, error, refetch } = useApi<any>(async () => (await api.getFeedbackSettings()).data);
  const [busy, setBusy] = useState(false);
  const [urls, setUrls] = useState<Record<string, string> | null>(null);
  const [note, setNote] = useState('');
  const canManage = !!data?.canManage;
  const current = urls ?? (data ? { googleUrl: data.googleUrl, bbbUrl: data.bbbUrl, trustedprosUrl: data.trustedprosUrl } : null);
  const dirty = !!data && !!urls && (['googleUrl', 'bbbUrl', 'trustedprosUrl'] as const).some((k) => (urls[k] ?? '') !== (data[k] ?? ''));

  const save = async (patch: Record<string, unknown>, done?: string) => {
    setBusy(true); setNote('');
    try { await api.updateFeedbackSettings(patch as any); await refetch(); if (done) setNote(done); return true; }
    catch (e: any) { setNote(e?.message || 'Could not save that.'); return false; }
    finally { setBusy(false); }
  };
  const field: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontSize: '12px', borderRadius: '6px', background: T.panel2, color: T.text, border: `1px solid ${T.border}` };
  const toggle = (key: 'enabled' | 'sendEmail' | 'sendText', label: string, strong?: boolean) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: strong ? 600 : 400, color: T.text, cursor: canManage ? 'pointer' : 'default' }}>
      <input type="checkbox" checked={!!data?.[key]} disabled={!canManage || busy} onChange={(e) => save({ [key]: e.target.checked })} />
      {label}
    </label>
  );

  return (
    <Card Icon={Icons.star} title="Feedback" full>
      {error && <div style={{ fontSize: '11px', color: T.amber, marginBottom: '8px' }}>Feedback settings could not be loaded: {String(error)}</div>}
      {data && current && (
        <>
          {toggle('enabled', 'Ask clients for feedback after their move', true)}
          <div style={{ fontSize: '11px', color: T.dim, margin: '4px 0 10px 24px', lineHeight: 1.5 }}>
            When a job is moved to Closed, the client is sent a feedback request at 10:00 the next day, and one reminder email 7 days later if they
            have not rated. Moving the job out of Closed before then cancels it. 4–5 stars are offered the review sites below; 1–3 stars notify
            the CS rep (or the admins).
          </div>
          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginLeft: '24px', marginBottom: '14px' }}>
            {toggle('sendEmail', 'Send by email')}
            {toggle('sendText', 'Send by text')}
          </div>

          <div style={{ fontSize: '11px', fontWeight: 600, color: T.muted, marginBottom: '6px' }}>Review links</div>
          {([['googleUrl', 'Google'], ['bbbUrl', 'Better Business Bureau'], ['trustedprosUrl', 'TrustedPros']] as const).map(([k, label]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: T.text, minWidth: '160px' }}>{label}</span>
              <input value={current[k] ?? ''} disabled={!canManage || busy} placeholder="https://… (blank: not shown)"
                     onChange={(e) => setUrls({ ...current, [k]: e.target.value })} style={{ ...field, flex: 1, minWidth: '220px' }} />
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
            {canManage && (
              <button disabled={!dirty || busy} onClick={async () => { if (await save(current, 'Review links saved.')) setUrls(null); }}
                      style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', cursor: dirty ? 'pointer' : 'default', background: T.accentFill, color: 'var(--crm-on-fill)', opacity: dirty ? 1 : 0.5 }}>
                Save links
              </button>
            )}
            {dirty && <button onClick={() => setUrls(null)} style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', cursor: 'pointer', background: T.panel2, color: T.muted, border: `1px solid ${T.border}` }}>Discard</button>}
            {note && <span style={{ fontSize: '11px', color: T.muted }}>{note}</span>}
            {!canManage && <span style={{ fontSize: '11px', color: T.dim }}>Only an admin can change these.</span>}
          </div>
        </>
      )}
    </Card>
  );
}

function TextTemplatesCard() {
  const api = useApiClient();
  const { data, error, refetch } = useApi<any>(async () => (await api.getTextTemplates()).data);
  const [editing, setEditing] = useState<{ id?: string; name: string; body: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const bodyRef = React.useRef<HTMLTextAreaElement | null>(null);

  const list: any[] = data?.templates ?? [];
  const canManage = !!data?.canManage;

  const saveGuard = useAction();
  const save = () => saveGuard.run(async () => {
    if (!editing) return;
    setBusy(true);
    setProblem('');
    try {
      if (editing.id) await api.updateTextTemplate(editing.id, { name: editing.name, body: editing.body });
      else await api.createTextTemplate({ name: editing.name, body: editing.body });
      setEditing(null);
      refetch();
    } catch (e: any) {
      setProblem(e?.message || 'Could not save that template.');
    } finally {
      setBusy(false);
    }
  });

  const remove = async (t: any) => {
    if (!window.confirm(`Delete the text template "${t.name}"?`)) return;
    try { await api.deleteTextTemplate(t.id); refetch(); }
    catch (e: any) { window.alert(e?.message || 'Could not delete that template.'); }
  };

  const move = async (index: number, delta: number) => {
    const ids = list.map((t) => t.id);
    const j = index + delta;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    try { await api.reorderTextTemplates(ids); refetch(); }
    catch (e: any) { window.alert(e?.message || 'Could not reorder.'); }
  };

  /** Insert a {{token}} at the caret. */
  const insertToken = (token: string) => {
    if (!editing) return;
    const el = bodyRef.current;
    const at = el ? el.selectionStart : editing.body.length;
    const end = el ? el.selectionEnd : at;
    const before = editing.body.slice(0, at);
    const pad = before && !/\s$/.test(before) ? ' ' : '';
    const body = `${before}${pad}${token} ${editing.body.slice(end)}`;
    setEditing({ ...editing, body });
    requestAnimationFrame(() => {
      const pos = before.length + pad.length + token.length + 1;
      el?.focus();
      el?.setSelectionRange(pos, pos);
    });
  };

  const preview = editing ? fillTextTemplate(editing.body, SAMPLE_TEXT_VALUES).text : '';
  const smallBtn: React.CSSProperties = { padding: '2px 7px', fontSize: '11px', borderRadius: '5px', cursor: 'pointer', background: T.panel2, color: T.text, border: `1px solid ${T.border}` };
  const field: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontSize: '12px', borderRadius: '6px', background: T.panel2, color: T.text, border: `1px solid ${T.border}` };

  return (
    <Card Icon={Icons.message} title="Text templates" full>
      <AutoTextSettings templates={list} />
      {error && <div style={{ fontSize: '11px', color: T.amber, marginBottom: '8px' }}>Templates could not be loaded: {String(error)}</div>}
      <div style={{ fontSize: '11px', color: T.dim, marginBottom: '10px', lineHeight: 1.5 }}>
        Offered in the Text box on a lead's Sales page. The rep can edit the filled-in text before sending, and anything missing shows as [no …] and must be fixed first.
        {!canManage && ' Only an admin can change these.'}
      </div>

      {list.length === 0 && !error && empty('No text templates yet.')}
      {list.map((t, i) => (
        <div key={t.id} style={{ ...rowStyle, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '12px', color: T.text, fontWeight: 600 }}>{t.name}</div>
            <div style={{ fontSize: '11px', color: T.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.body}</div>
          </div>
          {canManage ? (
            <>
              <button style={smallBtn} disabled={i === 0} onClick={() => move(i, -1)} title="Move up" aria-label="Move up">↑</button>
              <button style={smallBtn} disabled={i === list.length - 1} onClick={() => move(i, 1)} title="Move down" aria-label="Move down">↓</button>
              <button style={iconBtn} onClick={() => { setProblem(''); setEditing({ id: t.id, name: t.name, body: t.body }); }} title="Edit"><Icons.edit size={15} /></button>
              <button style={{ ...iconBtn, color: T.red }} onClick={() => remove(t)} title="Delete"><Icons.delete size={15} /></button>
            </>
          ) : (
            <button style={iconBtn} onClick={() => setEditing({ id: t.id, name: t.name, body: t.body })} title="View"><Icons.eye size={15} /></button>
          )}
        </div>
      ))}

      {canManage && !editing && (
        <button style={{ ...smallBtn, padding: '6px 12px', fontSize: '12px', marginTop: '10px' }} onClick={() => { setProblem(''); setEditing({ name: '', body: '' }); }}>
          + New text template
        </button>
      )}

      {editing && (
        <div style={{ marginTop: '12px', padding: '12px', border: `1px solid ${T.border}`, borderRadius: '8px', background: T.panel, display: 'grid', gap: '8px' }}>
          <input value={editing.name} readOnly={!canManage} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Template name" style={field} />
          <textarea ref={bodyRef} value={editing.body} readOnly={!canManage} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                    rows={5} placeholder="Hi {{client_first_name}}, …" style={{ ...field, resize: 'vertical', fontSize: '13px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: editing.body.length > 1000 ? T.red : T.dim, gap: '8px', flexWrap: 'wrap' }}>
            <span>{editing.body.length} / 1000 as written · {preview.length} filled with the sample</span>
          </div>
          {canManage && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {SMS_MERGE_FIELDS.map((f) => (
                <button key={f.key} type="button" style={smallBtn} onClick={() => insertToken(f.token)} title={f.token}>{f.label}</button>
              ))}
            </div>
          )}
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: T.muted, marginBottom: '4px' }}>Preview — a sample long-distance lead</div>
            <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', background: 'var(--crm-comm-text-tint)', border: '1px solid var(--crm-comm-text-line)', borderRadius: '8px', padding: '8px 10px', color: T.text }}>
              {preview || <span style={{ color: T.dim }}>(empty)</span>}
            </div>
          </div>
          {problem && <div style={{ fontSize: '12px', color: T.red }}>{problem}</div>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button style={{ ...smallBtn, padding: '6px 12px', fontSize: '12px' }} onClick={() => setEditing(null)}>{canManage ? 'Cancel' : 'Close'}</button>
            {canManage && (
              <button disabled={busy || !editing.name.trim() || !editing.body.trim()} onClick={save}
                      style={{ padding: '6px 14px', fontSize: '12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, background: T.accentFill, color: 'var(--crm-on-fill)', border: 'none', opacity: busy ? 0.6 : 1 }}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function EmailTemplatesCard() {
  const api = useApiClient();
  const { data, error, refetch } = useApi<any>(async () => (await api.getEmailTemplates()).data);
  const [editing, setEditing] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  const shared: any[] = data?.shared ?? [];
  const mine: any[] = data?.mine ?? [];
  const canManageShared = !!data?.canManageShared;

  const saveGuard = useAction(); // one at a time: a second click is ignored
  const save = (t: any) => saveGuard.run(() => saveNow(t));
  const saveNow = async (t: any) => {
    setBusy(true);
    try {
      if (t.id) await api.updateEmailTemplate(t.id, { name: t.name, subject: t.subject, body: t.body });
      else await api.createEmailTemplate({ name: t.name, subject: t.subject, body: t.body, shared: t.shared });
      setEditing(null);
      refetch();
    } catch (e: any) {
      window.alert(e?.message || 'Could not save that template.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t: any) => {
    if (!window.confirm(`Delete the template "${t.name}"?`)) return;
    try { await api.deleteEmailTemplate(t.id); refetch(); }
    catch (e: any) { window.alert(e?.message || 'Could not delete that template.'); }
  };

  const row = (t: any, editable: boolean) => (
    <div key={t.id} style={{ ...rowStyle, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: T.text }}>{t.name}</div>
        <div style={{ fontSize: '11px', color: T.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {t.subject || <span style={{ color: T.dim }}>no subject line</span>}
        </div>
      </div>
      {editable ? (
        <>
          <button style={iconBtn} onClick={() => setEditing(t)} title="Edit"><Icons.edit size={15} /></button>
          <button style={{ ...iconBtn, color: T.red }} onClick={() => remove(t)} title="Delete"><Icons.delete size={15} /></button>
        </>
      ) : (
        <button style={iconBtn} onClick={() => setEditing({ ...t, readOnly: true })} title="View"><Icons.eye size={15} /></button>
      )}
    </div>
  );

  return (
    <Card Icon={Icons.email} title="Email templates" full>
      {error && (
        <div style={{ fontSize: '11px', color: T.amber, marginBottom: '8px', lineHeight: 1.5 }}>
          Templates could not be loaded: {error}
        </div>
      )}

      <div style={{ fontSize: '11px', color: T.dim, marginBottom: '10px', lineHeight: 1.5 }}>
        Merge fields go in as <span style={{ fontFamily: T.mono }}>{'{{client_first_name}}'}</span> and fill in when
        the template is inserted. Anything with no value stays visible and highlighted in the compose box, and blocks
        the send until it is dealt with — a client should never receive the gap.
      </div>

      <AutomaticEmails />

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '14px 0 6px' }}>
        <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', color: T.dim, fontWeight: 700 }}>Shared</span>
        {canManageShared && (
          <button style={{ ...ghostBtn, padding: '4px 10px' }} onClick={() => setEditing({ name: '', subject: '', body: '', shared: true })}>
            + Add shared
          </button>
        )}
        {!canManageShared && <span style={{ fontSize: '11px', color: T.dim }}>managed by an admin</span>}
      </div>
      {shared.length === 0 && empty('No shared templates yet.')}
      {shared.map((t) => row(t, canManageShared))}

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '14px 0 6px' }}>
        <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', color: T.dim, fontWeight: 700 }}>Mine</span>
        <button style={{ ...ghostBtn, padding: '4px 10px' }} onClick={() => setEditing({ name: '', subject: '', body: '', shared: false })}>
          + Add mine
        </button>
        <span style={{ fontSize: '11px', color: T.dim }}>only you see these</span>
      </div>
      {mine.length === 0 && empty('None yet — "Save as my template" in the compose box puts one here.')}
      {mine.map((t) => row(t, true))}

      {editing && (
        <Modal
          title={editing.readOnly ? editing.name : editing.id ? 'Edit template' : editing.shared ? 'New shared template' : 'New template'}
          onClose={() => setEditing(null)}
          onSave={() => (editing.readOnly ? setEditing(null) : save(editing))}
          saveDisabled={busy || (!editing.readOnly && !String(editing.name ?? '').trim())}
        >
          <Field label="Name">
            <input style={inputStyle} value={editing.name ?? ''} disabled={editing.readOnly}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          </Field>
          <Field label="Subject">
            <input style={inputStyle} value={editing.subject ?? ''} disabled={editing.readOnly}
              onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />
          </Field>
          <Field label="Body">
            <textarea
              style={{ ...inputStyle, minHeight: '180px', resize: 'vertical', fontFamily: 'inherit' }}
              value={editing.body ?? ''} disabled={editing.readOnly}
              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
            />
          </Field>
          <div style={{ fontSize: '11px', color: T.dim, lineHeight: 1.6 }}>
            Available fields: {MERGE_FIELDS.map((f) => f.token).join(' ')}
          </div>
        </Modal>
      )}
    </Card>
  );
}

/**
 * AUTOMATIC EMAILS — the wording of emails the CRM sends by itself (today: the
 * client's booking confirmation). Admin-edited; everyone can read. What each one
 * shows beyond the wording — {{booking_details}} — is decided in code
 * (backend/src/lib/bookingConfirmation.ts). Saving the default text, or an empty
 * body, puts the email back on the default.
 */
function AutomaticEmails() {
  const api = useApiClient();
  const { data, error, refetch } = useApi<any>(async () => (await api.getSystemEmails()).data);
  const [editing, setEditing] = useState<any | null>(null);
  const saveGuard = useAction();
  const canEdit = !!data?.canEdit;
  const emails: any[] = data?.emails ?? [];

  const save = () => saveGuard.run(async () => {
    try {
      await api.updateSystemEmail(editing.key, { subject: editing.subject, body: editing.body });
      setEditing(null);
      refetch();
    } catch (e: any) {
      window.alert(e?.message || 'Could not save that email.');
    }
  });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '2px 0 6px' }}>
        <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', color: T.dim, fontWeight: 700 }}>Automatic emails</span>
        <span style={{ fontSize: '11px', color: T.dim }}>sent by the CRM itself{canEdit ? '' : ' · managed by an admin'}</span>
      </div>
      {error && <div style={{ fontSize: '11px', color: T.amber, marginBottom: '6px' }}>Could not load: {error}</div>}
      {emails.map((m) => (
        <div key={m.key} style={{ ...rowStyle, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '12px', color: T.text }}>
              {m.label}
              <span style={{ marginLeft: '8px', fontSize: '10px', color: T.dim }}>{m.isDefault ? 'default wording' : 'edited'}</span>
            </div>
            <div style={{ fontSize: '11px', color: T.muted }}>{m.subject} — {m.description}</div>
          </div>
          <button style={iconBtn} onClick={() => setEditing({ ...m, readOnly: !canEdit })} title={canEdit ? 'Edit' : 'View'}>
            {canEdit ? <Icons.edit size={15} /> : <Icons.eye size={15} />}
          </button>
        </div>
      ))}
      {editing && (
        <Modal
          title={editing.label}
          onClose={() => setEditing(null)}
          onSave={() => (editing.readOnly ? setEditing(null) : save())}
          saveDisabled={saveGuard.pending}
        >
          <Field label="Subject">
            <input style={inputStyle} value={editing.subject ?? ''} disabled={editing.readOnly}
              onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />
          </Field>
          <Field label="Body">
            <textarea
              style={{ ...inputStyle, minHeight: '260px', resize: 'vertical', fontFamily: 'inherit' }}
              value={editing.body ?? ''} disabled={editing.readOnly}
              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
            />
          </Field>
          <div style={{ fontSize: '11px', color: T.dim, lineHeight: 1.6 }}>
            A blank line starts a new paragraph. Available fields: {(editing.fields ?? []).join(' ')}.{' '}
            {'{{booking_details}}'} on its own line is the "Please Confirm We Have The Right Information On File" block —
            name, phone and addresses, then the move date (local) or the pickup and drop-off ranges (long distance);
            an empty line is left out. The rep's name and title sign it.
            {!editing.readOnly && (
              <> {' '}
                <button type="button" style={{ ...ghostBtn, padding: '2px 8px', marginLeft: '4px' }}
                  onClick={() => setEditing({ ...editing, subject: editing.default.subject, body: editing.default.body })}>
                  Restore default
                </button>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

/**
 * RINGCENTRAL (plan step 6) — is text sync on, how, and is each store current.
 *
 * MODE FIRST, because it explains everything below it:
 *   Off       RINGCENTRAL_SYNC_ENABLED is not "on" (normal outside a test window
 *             on staging: staging and production share one RingCentral account).
 *   Poll mode no RINGCENTRAL_WEBHOOK_URL — the 60 s tick alone (local).
 *   Webhook   the subscription delivers events; the tick is the backup.
 * Then one row per store read (101, 103): last sync, last error. Nothing here
 * can print a secret: the server sends booleans and names.
 */
function RingCentralCard() {
  const api = useApiClient();
  const { data, loading, refetch } = useApi<any>(async () => (await api.getRingCentralStatus()).data);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const act = async (what: 'sync' | 'subscribe') => {
    setBusy(what);
    setNote('');
    try {
      const res: any = (what === 'sync' ? await api.syncRingCentralNow() : await api.resubscribeRingCentral()).data;
      if (what === 'subscribe') setNote(`Subscription: ${res?.result ?? 'done'}.`);
      else if (!res?.started) setNote('A sync pass is already running; it will run once more when it finishes.');
      else {
        const rs: any[] = res?.pass?.results ?? [];
        const cl = res?.pass?.calls;
        setNote((rs.length
          ? rs.map((r) => `ext ${r.ext}: ${r.error ? `error — ${r.error}` : `${r.records} texts checked, ${r.stored} new`}`).join(' · ')
          : res?.pass?.error ? `Error: ${res.pass.error}` : 'Done.')
          + (cl ? ` · call log: ${cl.error ? `error — ${cl.error}` : `${cl.records} calls checked, ${cl.missed} missed, ${cl.corrected} corrected, ${cl.logged ?? 0} app / desk-phone calls logged`}` : ''));
      }
      await refetch();
    } catch (e: any) {
      setNote(e?.message || 'That did not work.');
    } finally {
      setBusy(null);
    }
  };

  const MODE: Record<string, { label: string; color: string }> = {
    off: { label: 'Sync OFF', color: T.dim },
    poll: { label: 'Poll mode (no webhook)', color: T.amber },
    webhook: { label: 'Webhook', color: T.green },
  };
  const mode = MODE[data?.mode ?? 'off'] ?? MODE.off;
  const sub = data?.subscription;
  const when = (iso: string | null | undefined) => (iso ? formatDateTime(iso) : '—');
  const btn: React.CSSProperties = { padding: '6px 12px', borderRadius: '7px', fontSize: '12px', cursor: 'pointer', background: T.panel2, color: T.text, border: `1px solid ${T.border}` };

  return (
    <Card Icon={Icons.message} title="RingCentral texts" full>
      <div style={{ background: T.panel2, border: `1px solid ${T.border}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center', fontSize: '12px' }}>
          <span style={{ color: mode.color, fontWeight: 700 }}>{mode.label}</span>
          {data && <span style={{ color: data.configured ? T.green : T.red }}>Server login {data.configured ? 'configured' : `missing ${data.missing?.join(', ')}`}</span>}
          {data && data.mode !== 'off' && <span style={{ color: data.runnerRunning ? T.green : T.red }}>Tick {data.runnerRunning ? 'running (60s)' : 'not running'}{data.syncing ? ' · syncing now' : ''}</span>}
          {data && <span style={{ color: T.muted }}>Stores <span style={{ fontFamily: T.mono, color: T.text }}>{(data.readExtensions ?? []).join(', ')}</span></span>}
        </div>
        {data?.mode === 'off' && (
          <div style={{ fontSize: '11px', color: T.dim, marginTop: '6px', lineHeight: 1.5 }}>
            Staging and production share one RingCentral account, so sync is off except where it should pull texts in. Sending still works.
          </div>
        )}
        {data?.mode === 'poll' && (
          <div style={{ fontSize: '11px', color: T.dim, marginTop: '6px', lineHeight: 1.5 }}>
            No webhook address (RINGCENTRAL_WEBHOOK_URL), so RingCentral cannot call this server: it checks both stores every 60 seconds instead.
          </div>
        )}
        {data?.error && <div style={{ fontSize: '11px', color: T.red, marginTop: '6px' }}>{data.error}</div>}
      </div>

      {loading && empty('Loading…')}

      {data?.mode === 'webhook' && (
        <div style={{ fontSize: '12px', marginBottom: '12px', lineHeight: 1.7 }}>
          <div><span style={{ color: T.muted }}>Webhook</span> <span style={{ fontFamily: T.mono, overflowWrap: 'anywhere' }}>{data.webhookUrl}</span>{!data.verificationTokenSet && <span style={{ color: T.red }}> · verification token NOT set</span>}</div>
          <div>
            <span style={{ color: T.muted }}>Subscription</span>{' '}
            <span style={{ color: sub?.subscription_status === 'Active' ? T.green : sub?.subscription_status ? T.red : T.dim, fontWeight: 600 }}>{sub?.subscription_status ?? 'none yet'}</span>
            {sub?.subscription_expires_at && <span style={{ color: T.muted }}> · expires {when(sub.subscription_expires_at)}</span>}
            <span style={{ color: T.muted }}> · last event {when(sub?.last_event_at)}</span>
          </div>
          {sub?.last_error && <div style={{ color: T.red }}>{sub.last_error}</div>}
        </div>
      )}

      {(data?.extensions ?? []).length > 0 && (
        <div style={{ display: 'grid', gap: '6px', marginBottom: '12px' }}>
          {data.extensions.map((e: any) => (
            <div key={e.id} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'baseline', fontSize: '12px', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
              <span style={{ fontWeight: 700, minWidth: '70px' }}>ext {e.number}</span>
              <span style={{ color: T.muted }}>{e.name}</span>
              <span style={{ color: T.muted }}>last sync <span style={{ color: T.text }}>{when(e.state?.last_sync_at)}</span></span>
              <span style={{ color: e.state?.last_error ? T.red : e.state?.has_sync_token ? T.green : T.dim }}>
                {e.state?.last_error ? `Error: ${e.state.last_error}` : e.state?.has_sync_token ? 'OK' : 'Never synced'}
              </span>
            </div>
          ))}
        </div>
      )}

      {data?.mode && data.mode !== 'off' && (
        <div style={{ fontSize: '12px', marginBottom: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700 }}>Missed calls</span>
          <span style={{ color: T.muted }}>call log read to <span style={{ color: T.text }}>{when(data.callLog?.last_sync_at)}</span></span>
          <span style={{ color: data.callLog?.last_error ? T.red : data.callLog?.last_sync_at ? T.green : T.dim }}>
            {data.callLog?.last_error ? `Error: ${data.callLog.last_error}` : data.callLog?.last_sync_at ? 'OK' : 'Not yet read'}
          </span>
        </div>
      )}

      <ExtensionMap />

      <IgnoredNumbers />

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button style={btn} disabled={!!busy || !data || data.mode === 'off'} onClick={() => act('sync')}>{busy === 'sync' ? 'Syncing…' : 'Sync now'}</button>
        {data?.mode === 'webhook' && <button style={btn} disabled={!!busy} onClick={() => act('subscribe')}>{busy === 'subscribe' ? 'Working…' : 'Re-subscribe'}</button>}
        <button style={btn} disabled={!!busy} onClick={() => refetch()}>Refresh</button>
        {note && <span style={{ fontSize: '12px', color: T.muted }}>{note}</span>}
      </div>
    </Card>
  );
}

/**
 * WHO IS WHICH EXTENSION (plan step 9). Each CRM user against their RingCentral
 * extension (Daryl → 101, Jacob → 103). The map credits texts sent from the
 * RingCentral app, names who a missed call rang, and routes a Messages item
 * with no lead owner to the person whose line it came in on. One extension,
 * one person. Admin only (this whole tab is; the endpoints check too).
 */
function ExtensionMap() {
  const api = useApiClient();
  const { data, error, refetch } = useApi<any>(async () => (await api.getRingCentralExtensions()).data);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const save = async (userId: string, extId: string) => {
    setBusy(userId);
    setNote('');
    try { await api.setRingCentralUserMap(userId, extId || null); await refetch(); }
    catch (e: any) { setNote(e?.message || 'Could not save that.'); }
    finally { setBusy(null); }
  };
  const exts: any[] = data?.extensions ?? [];
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>People and extensions</div>
      {error && <div style={{ fontSize: '11px', color: T.red }}>{String(error)}</div>}
      {!error && !data && empty('Loading…')}
      {(data?.users ?? []).map((u: any) => (
        <div key={u.id} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', fontSize: '12px', padding: '5px 0', borderBottom: `1px solid ${T.border}` }}>
          <span style={{ minWidth: '140px', fontWeight: 600 }}>{u.name}</span>
          <span style={{ color: T.dim }}>{u.role}</span>
          <select value={u.extension_id ?? ''} disabled={busy === u.id} onChange={(e) => save(u.id, e.target.value)}
                  style={{ marginLeft: 'auto', minWidth: '200px', maxWidth: '100%', padding: '5px 8px', fontSize: '12px', borderRadius: '6px', background: T.panel2, color: T.text, border: `1px solid ${T.border}` }}>
            <option value="">— no extension —</option>
            {exts.map((e) => <option key={e.id} value={e.id}>{e.number} · {e.name}{e.status && e.status !== 'Enabled' ? ` (${e.status})` : ''}</option>)}
          </select>
        </div>
      ))}
      {note && <div style={{ fontSize: '11px', color: T.red, marginTop: '4px' }}>{note}</div>}
    </div>
  );
}

/**
 * Numbers marked "Spam — ignore from now on" in the unmatched dialog (069).
 * Their calls and texts are stored but kept out of Messages and the feed.
 * Un-ignoring affects what comes in next; what was dismissed stays dismissed.
 */
function IgnoredNumbers() {
  const api = useApiClient();
  const { data, error, refetch } = useApi<any[]>(async () => (await api.getRingCentralIgnored()).data ?? []);
  const [busy, setBusy] = useState<string | null>(null);
  const unignore = async (id: string) => {
    setBusy(id);
    try { await api.unignoreRingCentralNumber(id); await refetch(); }
    catch (e: any) { window.alert(e?.message || 'Could not un-ignore that number.'); }
    finally { setBusy(null); }
  };
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Ignored numbers</div>
      {error && <div style={{ fontSize: '11px', color: T.red }}>{String(error)}</div>}
      {!error && (data ?? []).length === 0 && empty('None. A number marked "Spam — ignore from now on" is listed here.')}
      {(data ?? []).map((r: any) => (
        <div key={r.id} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', fontSize: '12px', padding: '5px 0', borderBottom: `1px solid ${T.border}` }}>
          <span style={{ fontFamily: T.mono, fontWeight: 600 }}>{r.number}</span>
          <span style={{ color: T.muted }}>{formatDateTime(r.created_at)}{r.created_by ? ` · ${r.created_by}` : ''}</span>
          <button onClick={() => unignore(r.id)} disabled={busy === r.id}
                  style={{ marginLeft: 'auto', padding: '3px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', background: T.panel2, color: T.text, border: `1px solid ${T.border}` }}>
            {busy === r.id ? 'Working…' : 'Un-ignore'}
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * EMAIL SYNC (G1a) — one row per mailbox, and the reason for every silence.
 *
 * THE THREE STATES ARE THE POINT. "Never synced", "synced and matched nothing"
 * and "errored" all look identical from a lead file — no email on it — and they
 * have completely different fixes. So each mailbox states which one it is, and a
 * mailbox that is not being polled at all says why instead of being left out.
 *
 * THE ENVIRONMENT IS SHOWN FIRST, because staging and production share one
 * Google Workspace and only production polls it. Far and away the most likely
 * reason this panel is empty is that you are looking at the other environment,
 * and a page that made you guess would waste the afternoon.
 *
 * NOTHING HERE CAN PRINT THE SERVICE ACCOUNT KEY. The server sends a boolean.
 */
function EmailSyncCard() {
  const api = useApiClient();
  const { data, loading, refetch } = useApi<any>(async () => (await api.getGmailSyncStatus()).data);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string>('');

  const env = data?.environment;
  const mailboxes: any[] = data?.mailboxes ?? [];
  const backfill = data?.backfill;

  const resync = async (userId?: string) => {
    setBusy(userId ?? 'all');
    setNote('');
    try {
      const res = await api.resyncGmail(userId);
      const d: any = res.data;
      if (d?.started === false) {
        setNote(d.message || 'A sync pass is already running.');
      } else {
        const boxes: any[] = d?.mailboxes ?? [];
        const stored = boxes.reduce((n, b) => n + (b.stored ?? 0), 0);
        const matched = boxes.reduce((n, b) => n + (b.matched ?? 0), 0);
        const scanned = boxes.reduce((n, b) => n + (b.scanned ?? 0), 0);
        setNote(
          `Scanned ${scanned}, matched ${matched}, stored ${stored} new message${stored === 1 ? '' : 's'}.` +
          (d?.backfill?.processed ? ` Backfill: ${d.backfill.processed} job(s), ${d.backfill.stored} stored.` : '')
        );
      }
      await refetch();
    } catch (e: any) {
      setNote(e?.message || 'The resync failed.');
    } finally {
      setBusy(null);
    }
  };

  const toggleMailbox = async (m: any) => {
    setBusy(m.userId);
    try {
      await api.setGmailMailboxEnabled(m.userId, !m.enabled);
      await refetch();
    } catch (e: any) {
      window.alert(e?.message || 'Could not change that mailbox.');
    } finally {
      setBusy(null);
    }
  };

  const STATUS: Record<string, { label: string; color: string }> = {
    never_synced: { label: 'Never synced', color: T.dim },
    syncing: { label: 'Syncing…', color: T.amber },
    ok: { label: 'OK', color: T.green },
    error: { label: 'Error', color: T.red },
  };

  const when = (iso: string | null) => (iso ? formatDateTime(iso) : '—');

  return (
    <Card Icon={Icons.email} title="Email sync" full>
      {/* ---- environment: why this panel looks the way it does ---- */}
      <div style={{ background: T.panel2, border: `1px solid ${T.border}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center', fontSize: '12px' }}>
          <span style={{ color: T.muted }}>
            Environment <span style={{ fontFamily: T.mono, color: T.text }}>{env?.nodeEnv ?? '—'}</span>
          </span>
          <span style={{ color: env?.syncEnabled ? T.green : T.dim, fontWeight: 600 }}>
            Sync {env?.syncEnabled ? 'ON' : 'OFF'}
          </span>
          <span style={{ color: env?.sendEnabled ? T.green : T.dim, fontWeight: 600 }}>
            Send {env?.sendEnabled ? 'ON' : 'OFF'}
          </span>
          <span style={{ color: env?.pollerRunning ? T.green : T.dim }}>
            Poller {env?.pollerRunning ? 'running (60s)' : 'not running'}
          </span>
          <span style={{ color: env?.serviceAccountConfigured ? T.green : T.red }}>
            Google key {env?.serviceAccountConfigured ? 'present' : 'missing'}
          </span>
          {data && (
            <span style={{ color: T.muted, marginLeft: 'auto', fontFamily: T.mono }}>
              {data.totals?.messages ?? 0} stored · {data.totals?.unseen ?? 0} unseen
            </span>
          )}
        </div>

        {env?.allowlist?.length > 0 && (
          <div style={{ fontSize: '11px', color: T.amber, marginTop: '6px' }}>
            Restricted to {env.allowlist.join(', ')} — this deployment may only touch those mailboxes.
          </div>
        )}
        {env && !env.syncEnabled && (
          <div style={{ fontSize: '11px', color: T.dim, marginTop: '6px', lineHeight: 1.5 }}>
            Staging and production share one Google Workspace, so only one of them may poll it. This is
            the expected state everywhere but production — nothing is broken.
          </div>
        )}
        {env?.configError && (
          <div style={{ fontSize: '11px', color: T.red, marginTop: '6px', lineHeight: 1.5 }}>{env.configError}</div>
        )}
        {env?.serviceAccountError && (
          <div style={{ fontSize: '11px', color: T.red, marginTop: '6px', lineHeight: 1.5 }}>{env.serviceAccountError}</div>
        )}
      </div>

      {/* ---- the mailboxes ---- */}
      {loading && empty('Loading…')}
      {!loading && mailboxes.length === 0 && empty('No users to sync.')}

      {mailboxes.map((m) => {
        const meta = STATUS[m.status] ?? STATUS.never_synced;
        return (
          <div key={m.userId} style={{ ...rowStyle, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '220px' }}>
              <div style={{ fontSize: '12px', color: T.text }}>
                {m.name}
                {!m.eligible && (
                  <span style={{ marginLeft: '8px', fontSize: '10px', color: T.dim, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    not polled
                  </span>
                )}
              </div>
              <div style={{ fontSize: '11px', color: T.muted, fontFamily: T.mono }}>{m.email}</div>
              {/* A mailbox that is not polled says why. Absence is never the message. */}
              {m.skipReason && (
                <div style={{ fontSize: '11px', color: T.dim, marginTop: '3px' }}>{m.skipReason}</div>
              )}
              {m.lastError && (
                <div style={{ fontSize: '11px', color: T.red, marginTop: '3px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {m.lastError}
                  <span style={{ color: T.dim }}> · {when(m.lastErrorAt)}</span>
                </div>
              )}
            </div>

            <div style={{ width: '110px', flexShrink: 0 }}>
              <div style={{ fontSize: '12px', color: meta.color, fontWeight: 600 }}>{meta.label}</div>
              <div style={{ fontSize: '11px', color: T.dim }}>{when(m.lastSyncedAt)}</div>
            </div>

            <div style={{ width: '132px', flexShrink: 0, textAlign: 'right' }}>
              <div style={{ fontSize: '12px', fontFamily: T.mono, color: T.text }}>
                {m.messages} message{m.messages === 1 ? '' : 's'}
              </div>
              {/* The last-run pair, because a poll that is alive and finding nothing
                  looks exactly like a poll that has stopped if you only show totals. */}
              <div style={{ fontSize: '11px', color: T.dim, fontFamily: T.mono }}>
                last run {m.lastMatched}/{m.lastScanned}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button
                type="button"
                style={{ ...ghostBtn, padding: '6px 10px', color: m.enabled ? T.green : T.muted }}
                disabled={busy === m.userId}
                onClick={() => toggleMailbox(m)}
                title={m.enabled ? 'Stop syncing this mailbox' : 'Start syncing this mailbox'}
              >
                {m.enabled ? 'On' : 'Off'}
              </button>
              <button
                type="button"
                style={{ ...ghostBtn, padding: '6px 10px' }}
                disabled={!m.eligible || !env?.syncEnabled || busy !== null}
                onClick={() => resync(m.userId)}
              >
                {busy === m.userId ? 'Syncing…' : 'Resync now'}
              </button>
            </div>
          </div>
        );
      })}

      {/* ---- backfill queue: attempts and the last error, before it gives up ---- */}
      {backfill && (backfill.pending > 0 || backfill.running > 0 || backfill.errored > 0 || (backfill.recent ?? []).length > 0) && (
        <div style={{ borderTop: `1px solid ${T.border}`, marginTop: '10px', paddingTop: '10px' }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', color: T.dim, fontWeight: 700, marginBottom: '6px' }}>
            Address backfill · {backfill.pending} pending · {backfill.running} running ·{' '}
            <span style={{ color: backfill.errored ? T.red : T.dim }}>{backfill.errored} failed</span>
          </div>
          <div style={{ fontSize: '11px', color: T.dim, marginBottom: '8px', lineHeight: 1.5 }}>
            One job per address, searching every mailbox {env?.initialSyncDays ?? 90} days back — this is what
            finds mail that arrived before the lead existed. A job stops after three attempts and says so
            rather than retrying forever behind a queue that never empties.
          </div>
          {(backfill.recent ?? []).map((j: any) => (
            <div key={j.id} style={{ ...rowStyle, marginBottom: '4px' }}>
              <span style={{ flex: 1, fontSize: '11px', fontFamily: T.mono, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {j.email}
              </span>
              <span style={{ fontSize: '11px', color: T.dim, flexShrink: 0 }}>{j.reason}</span>
              <span style={{ fontSize: '11px', flexShrink: 0, color: j.status === 'error' ? T.red : j.status === 'done' ? T.green : T.amber }}>
                {humanize(j.status)}
              </span>
              <span style={{ fontSize: '11px', color: T.dim, flexShrink: 0, width: '52px', textAlign: 'right' }}>
                {j.attempts} try{j.attempts === 1 ? '' : 's'}
              </span>
              {j.last_error && (
                <span style={{ fontSize: '11px', color: T.red, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.last_error}>
                  {j.last_error}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '12px' }}>
        <button
          type="button"
          style={primaryBtn}
          disabled={!env?.syncEnabled || busy !== null}
          onClick={() => resync()}
        >
          {busy === 'all' ? 'Syncing…' : 'Resync all mailboxes'}
        </button>
        <button type="button" style={ghostBtn} onClick={() => refetch()}>Refresh</button>
        {note && <span style={{ fontSize: '12px', color: T.muted }}>{note}</span>}
      </div>
    </Card>
  );
}

/**
 * INVENTORY MEDIA — the number, the worst offenders, and the clear-out.
 *
 * WHY A STORAGE FIGURE IS IN SETTINGS AT ALL. Client video is the first thing
 * this system stores that a customer can multiply without asking anyone. The free
 * plan's whole bucket allowance is 1 GB — five walkthroughs — and the symptom of
 * running out is uploads failing on a client's phone, which nobody reports to us;
 * they just stop. A number on a page somebody already visits turns that into
 * something seen coming.
 *
 * THE TOP-20 LIST IS THE ACTIONABLE HALF. A total says how bad it is; the list
 * with a stage beside each row says which ones are safe to clear, and that is the
 * judgment nobody should be making from a total alone.
 *
 * AUTO-CLEAR IS OFF AND ITS DRY RUN IS THE POINT. "What would this delete" is
 * answerable before the switch is ever flipped, and the answer is a file count
 * and a size, not a promise.
 */
/**
 * INVENTORY SEARCH — WHAT PEOPLE LOOKED FOR AND COULD NOT FIND (045).
 *
 * Every search that came back empty, on the client's three paths and in the rep
 * panel, counted by its normalised form so "Couches" and "couch" are one line.
 * "Ended as own item" is the louder signal: the client gave up and typed it in.
 *
 * Answering a term adds it to one catalogue item's search_terms, so the next
 * person who types it finds that item — and takes the term off this list.
 * Dismiss takes it off without a synonym (a typo of nothing, a test, a joke).
 */
function InventorySearchMissesCard({ isAdmin }: { isAdmin: boolean }) {
  const api = useApiClient();
  const [days, setDays] = useState(90);
  const { data: terms, error, refresh } = useApi<any[]>(
    async () => (await api.getInventorySearchMissesTop(days)).data || [],
    { dependencies: [days] },
  );
  const { data: catalogData } = useApi<{ catalog: any[] }>(async () => (await api.getInventoryCatalog()).data!);
  const prepared = useMemo(
    // Active items only (047): a term should point people at something they can pick.
    () => prepare((catalogData?.catalog ?? []).filter((c: any) => c.is_active !== false)
      .map((c: any) => ({ ...c, searchTerms: c.search_terms ?? [] }))),
    [catalogData],
  );
  const [open, setOpen] = useState<string | null>(null);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const resolve = async (t: any, itemId: string | null, itemName?: string) => {
    setBusy(true); setMsg(null);
    try {
      await api.resolveInventorySearchMiss({ term: t.term, term_normalized: t.termNormalized, item_id: itemId });
      setMsg(itemId ? `“${t.term}” now finds ${itemName}.` : `“${t.term}” dismissed.`);
      setOpen(null); setPick('');
      await refresh().catch(() => {});
    } catch (e: any) {
      setMsg(e?.message || 'Could not save that.');
    } finally { setBusy(false); }
  };

  const needsMigration = /045/.test(String(error ?? ''));
  const candidates = pick.trim() ? searchCatalog(prepared, pick).slice(0, 8).map((h) => h.item) : [];

  return (
    <Card Icon={Icons.search} title="Inventory search — not found" full>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '11px', color: T.dim, flex: 1, minWidth: '220px', lineHeight: 1.5 }}>
          Searches on the client inventory page and the rep panel that found nothing. Add a term to the
          item it meant and the next person who types it will find that item.
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ ...inputStyle, width: 'auto' }}>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>
      {msg && <div style={{ fontSize: '12px', color: T.green, marginBottom: '8px' }}>{msg}</div>}
      {needsMigration && <div style={{ fontSize: '12px', color: T.amber }}>Needs migration 045 — nothing is logged until it runs.</div>}
      {!needsMigration && error && <div style={{ fontSize: '12px', color: T.red }}>Could not load: {String(error)}</div>}
      {!error && terms && terms.length === 0 && (
        <div style={{ fontSize: '12px', color: T.dim }}>Nothing unanswered — every search in this period found something.</div>
      )}

      {(terms ?? []).map((t: any) => (
        <div key={t.termNormalized} style={{ ...rowStyle, flexDirection: 'column', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: T.text, fontWeight: 600 }}>“{t.term}”</span>
            <span style={{ fontSize: '11px', color: T.muted, fontFamily: T.mono }}>
              {t.count}× · {t.leads} lead{t.leads === 1 ? '' : 's'}
              {t.addedOwn > 0 && <span style={{ color: T.amber }}> · {t.addedOwn} ended as own item</span>}
            </span>
            <span style={{ fontSize: '11px', color: T.dim }}>last {formatDateTime(t.lastAt)}</span>
            {isAdmin && (
              <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                <button style={{ ...ghostBtn, padding: '6px 10px' }} disabled={busy} onClick={() => { setOpen(open === t.termNormalized ? null : t.termNormalized); setPick(''); }}>
                  Add as synonym…
                </button>
                <button style={{ ...ghostBtn, padding: '6px 10px' }} disabled={busy} onClick={() => resolve(t, null)}>Dismiss</button>
              </span>
            )}
          </div>
          {open === t.termNormalized && (
            <div style={{ marginTop: '8px' }}>
              <input
                autoFocus value={pick} onChange={(e) => setPick(e.target.value)}
                placeholder="Which item did they mean? — type to find it"
                style={inputStyle}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                {candidates.map((c: any) => (
                  <button key={c.id} disabled={busy} onClick={() => resolve(t, c.id, c.name)} style={{ ...ghostBtn, padding: '6px 10px', color: T.text }}>
                    {c.name}
                  </button>
                ))}
                {pick.trim() && candidates.length === 0 && <span style={{ fontSize: '11px', color: T.dim }}>No item matches that.</span>}
              </div>
            </div>
          )}
        </div>
      ))}
      {!isAdmin && (terms ?? []).length > 0 && (
        <div style={{ fontSize: '11px', color: T.dim, marginTop: '4px' }}>An admin can add these as synonyms.</div>
      )}
    </Card>
  );
}

function InventoryMediaCard({ company, onSave }: { company: any; onSave: (patch: any) => void }) {
  const api = useApiClient();
  const { data: usage, refetch: refetchUsage } = useApi<any>(async () => (await api.getInventoryMediaUsage()).data);
  const { data: top, refetch: refetchTop } = useApi<any[]>(async () => (await api.getInventoryMediaTopLeads(20)).data || []);
  const [plan, setPlan] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  const enabled = !!company.inv_media_autoclear_enabled;
  const daysDead = Number(company.inv_media_keep_days_dead ?? 30);
  const daysClosed = Number(company.inv_media_keep_days_closed ?? 120);

  const dryRun = async () => {
    setBusy(true);
    try { setPlan((await api.runInventoryMediaAutoClear(false)).data); }
    catch (e: any) { window.alert(e?.message || 'Could not work out what would be cleared.'); }
    finally { setBusy(false); }
  };

  const runForReal = async () => {
    if (!window.confirm(`Delete ${plan?.files ?? 0} file(s), ${plan?.label ?? '0 B'}? The files go for good; the records of them stay.`)) return;
    setBusy(true);
    try {
      const res = await api.runInventoryMediaAutoClear(true);
      setPlan(res.data);
      await Promise.all([refetchUsage(), refetchTop()]);
      window.alert(`Cleared ${res.data?.clearedFiles ?? 0} file(s), ${res.data?.clearedLabel ?? '0 B'}.`);
    } catch (e: any) { window.alert(e?.message || 'The clear-out failed.'); }
    finally { setBusy(false); }
  };

  const commitDays = (key: string, raw: string, current: number) => {
    const n = Math.trunc(Number(raw));
    if (!Number.isFinite(n) || n < 1) { window.alert('That has to be a whole number of days, one or more.'); return; }
    if (n !== current) onSave({ [key]: n });
  };

  return (
    <Card Icon={Icons.folder} title="Inventory photos & video" full>
      <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <Stat label="Total stored" value={usage ? usage.label : '—'} sub={usage ? `${usage.files} file${usage.files === 1 ? '' : 's'}` : ''} />
        <Stat label="In GB" value={usage ? `${usage.gb} GB` : '—'} sub="free plan allows 1 GB" />
        <Stat label="Photos" value={usage ? usage.photos.label : '—'} sub={usage ? `${usage.photos.files} file${usage.photos.files === 1 ? '' : 's'}` : ''} />
        <Stat label="Video" value={usage ? usage.videos.label : '—'} sub={usage ? `${usage.videos.files} file${usage.videos.files === 1 ? '' : 's'}` : ''} />
      </div>

      <div style={{ fontSize: '11px', color: T.dim, marginBottom: '10px', lineHeight: 1.5 }}>
        Caps per lead: 15 photos at 15 MB each, 3 videos at 200 MB each. Clients pick files from
        their phone — camera or camera roll, their choice — so a clip arrives at whatever size the
        phone filmed it, and a minute of 4K is around 400 MB. Until Supabase Pro is live, anything
        over about 50 MB fails at the platform rather than in our code, whatever the 200 MB cap
        says: roughly two minutes of 1080p, or fifteen seconds of 4K.
      </div>

      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', color: T.dim, fontWeight: 700, margin: '4px 0 6px' }}>
        Holding the most
      </div>
      {(top ?? []).length === 0 && empty('No media stored yet.')}
      {(top ?? []).map((r) => (
        <div key={r.leadId} style={{ ...rowStyle, marginBottom: '4px' }}>
          <span style={{ flex: 1, fontSize: '12px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.jobNo ? `#${r.jobNo} · ` : ''}{r.name}
          </span>
          <span style={{ fontSize: '11px', color: T.dim, flexShrink: 0 }}>{r.stage ?? '—'}</span>
          <span style={{ fontSize: '11px', color: T.dim, flexShrink: 0 }}>
            {r.photos}p / {r.videos}v
          </span>
          <span style={{ fontSize: '12px', fontFamily: T.mono, flexShrink: 0, width: '78px', textAlign: 'right' }}>{r.label}</span>
        </div>
      ))}

      <div style={{ borderTop: `1px solid ${T.border}`, marginTop: '12px', paddingTop: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <button
            type="button"
            onClick={() => onSave({ inv_media_autoclear_enabled: !enabled })}
            style={{ ...ghostBtn, color: enabled ? 'var(--crm-green)' : T.muted }}
          >
            {enabled ? 'Auto-clear is ON' : 'Auto-clear is OFF'}
          </button>
          <span style={{ fontSize: '11px', color: T.dim, lineHeight: 1.5 }}>
            Nothing is deleted while this is off, scheduled or not.
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <input
            style={{ ...inputStyle, width: '70px', fontFamily: T.mono }} {...numberInput(1)} min="1"
            defaultValue={String(daysDead)}
            onBlur={(e) => commitDays('inv_media_keep_days_dead', e.target.value, daysDead)}
          />
          <span style={{ fontSize: '11px', color: T.dim }}>days after a lead goes Lost or Bad Lead</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <input
            style={{ ...inputStyle, width: '70px', fontFamily: T.mono }} {...numberInput(1)} min="1"
            defaultValue={String(daysClosed)}
            onBlur={(e) => commitDays('inv_media_keep_days_closed', e.target.value, daysClosed)}
          />
          <span style={{ fontSize: '11px', color: T.dim }}>
            days after the move date on a Closed job — the contract gives 60 days to claim
          </span>
        </div>
        <div style={{ fontSize: '11px', color: T.dim, marginBottom: '10px', lineHeight: 1.5 }}>
          Opportunities and booked jobs are never cleared. The mail poller added in G1a is the only
          scheduled job in this system, and this clear-out is not on it — nothing here runs on its
          own: this button, or <span style={{ fontFamily: T.mono }}>scripts/clear-inventory-media.ts</span>.
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" style={ghostBtn} disabled={busy} onClick={dryRun}>
            {busy ? 'Working…' : 'What would be cleared?'}
          </button>
          {plan && (
            <>
              <span style={{ fontSize: '12px', color: T.muted }}>
                {plan.files} file{plan.files === 1 ? '' : 's'} · {plan.label} · {plan.leads} lead{plan.leads === 1 ? '' : 's'}
                {plan.applied ? ' · CLEARED' : ''}
              </span>
              {!plan.applied && plan.files > 0 && (
                <button type="button" style={{ ...primaryBtn, background: 'var(--crm-red-fill)' }} disabled={busy || !enabled} onClick={runForReal}>
                  Clear them now
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

const Stat = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div>
    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.6px', color: T.dim, fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: '17px', fontWeight: 700, color: T.text, fontFamily: T.mono }}>{value}</div>
    {sub && <div style={{ fontSize: '10.5px', color: T.dim }}>{sub}</div>}
  </div>
);

function CompanyCard({ company, onSave }: { company: any; onSave: (patch: any) => void }) {
  const fields: { key: string; label: string }[] = [
    { key: 'name', label: 'Company name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'web', label: 'Website' },
  ];
  return (
    <Card Icon={Icons.info} title="Company Info">
      {fields.map((f) => (
        <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <label style={{ fontSize: '11px', color: T.muted, width: '110px', flexShrink: 0 }}>{f.label}</label>
          <input
            style={inputStyle}
            defaultValue={company[f.key] ?? ''}
            onBlur={(e) => { if (e.target.value !== (company[f.key] ?? '')) onSave({ [f.key]: e.target.value }); }}
          />
        </div>
      ))}
      {!company.id && <div style={{ fontSize: '11px', color: T.dim, marginTop: '2px' }}>Loading company…</div>}
    </Card>
  );
}

// ---- Modal shell -----------------------------------------------------------

function Modal({ title, onClose, onSave, saveDisabled, children }: { title: string; onClose: () => void; onSave: () => void; saveDisabled?: boolean; children: React.ReactNode }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: '12px', padding: '20px', width: '420px', maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '14px', color: T.text }}>{title}</div>
        <div style={{ display: 'grid', gap: '10px' }}>{children}</div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={onSave} disabled={saveDisabled} style={{ ...primaryBtn, opacity: saveDisabled ? 0.5 : 1 }}>Save</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      <span style={{ fontSize: '10px', color: T.muted }}>{label}</span>
      {children}
    </label>
  );
}

// ---- Branch modal ----------------------------------------------------------

function BranchModal({ row, onSave, onClose }: { row?: any; onSave: (data: any, id?: string) => void; onClose: () => void }) {
  const [f, setF] = useState({ name: row?.name ?? '', province_state: row?.province_state ?? '', tax_rate: row?.tax_rate ?? 0 });
  return (
    <Modal title={row ? 'Edit branch' : 'Add branch'} onClose={onClose} saveDisabled={!String(f.name).trim()} onSave={() => onSave({ name: f.name, province_state: f.province_state, tax_rate: num(f.tax_rate) }, row?.id)}>
      <Field label="Name"><input style={inputStyle} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
      <Field label="Province / State"><input style={inputStyle} value={f.province_state} onChange={(e) => setF({ ...f, province_state: e.target.value })} /></Field>
      <Field label="Tax rate (%)"><input {...numberInput(0.01)} style={inputStyle} value={f.tax_rate} onChange={(e) => setF({ ...f, tax_rate: e.target.value })} /></Field>
    </Modal>
  );
}

// ---- Tariff modal ----------------------------------------------------------

function TariffModal({ row, fees, onSave, onClose }: { row?: any; fees: any[]; onSave: (data: any, id?: string) => void; onClose: () => void }) {
  const initialDefaults: string[] = Array.isArray(row?.default_fee_ids) ? row.default_fee_ids : [];
  const [defaults, setDefaults] = useState<string[]>(initialDefaults);
  const defaultsChanged = defaults.join(',') !== initialDefaults.join(',');
  const [f, setF] = useState({ name: row?.name ?? '', pricing_method: row?.pricing_method ?? 'weight', base_rate: row?.base_rate ?? 0, minimum_charge: row?.minimum_charge ?? 0, deposit_amount: row ? (row.deposit_amount ?? '') : 150, contractor_labour_pct: row?.contractor_labour_pct ?? 70 });
  const labourPct = String(f.contractor_labour_pct).trim() === '' ? 70 : num(f.contractor_labour_pct);
  return (
    <Modal title={row ? 'Edit tariff' : 'Add tariff'} onClose={onClose} saveDisabled={!String(f.name).trim() || labourPct < 0 || labourPct > 100} onSave={() => onSave({
      name: f.name, pricing_method: f.pricing_method, base_rate: num(f.base_rate), minimum_charge: num(f.minimum_charge),
      // Hourly only (056 deposit, blank = $150; §9.3 contractor labour %, blank = 70). Not sent for other tariffs.
      ...(f.pricing_method === 'hourly' ? { deposit_amount: String(f.deposit_amount).trim() === '' ? null : num(f.deposit_amount), contractor_labour_pct: labourPct } : {}),
      // Sent only when changed, so editing a tariff never depends on 060 having run.
      ...(defaultsChanged ? { default_fee_ids: defaults } : {}),
    }, row?.id)}>
      <Field label="Name"><input style={inputStyle} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
      <Field label="Pricing method">
        <select style={inputStyle} value={f.pricing_method} onChange={(e) => setF({ ...f, pricing_method: e.target.value })}>
          {['weight', 'cuft', 'flat', 'hourly'].map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </Field>
      <Field label="Base rate"><input {...numberInput(f.pricing_method === 'weight' ? STEP_PER_LB : 1)} style={inputStyle} value={f.base_rate} onChange={(e) => setF({ ...f, base_rate: e.target.value })} /></Field>
      {/* Hourly jobs have minimum hours, never a minimum charge (the engine ignores it). */}
      {f.pricing_method !== 'hourly' && (
        <Field label="Minimum charge"><input {...numberInput(1)} style={inputStyle} value={f.minimum_charge} onChange={(e) => setF({ ...f, minimum_charge: e.target.value })} /></Field>
      )}
      {/* Local deposit (056): what a local estimate asks for unless the rep changes it. */}
      {f.pricing_method === 'hourly' && (
        <Field label="Default deposit"><input {...numberInput(1)} min="0" placeholder="150" style={inputStyle} value={f.deposit_amount} onChange={(e) => setF({ ...f, deposit_amount: e.target.value })} /></Field>
      )}
      {/* Local contractor pay (§9.3): the contractor's share of labour, before any discount. */}
      {f.pricing_method === 'hourly' && (
        <Field label="Contractor labour % (of billed hours × rate)"><input {...numberInput(1)} min="0" max="100" placeholder="70" style={inputStyle} value={f.contractor_labour_pct} onChange={(e) => setF({ ...f, contractor_labour_pct: e.target.value })} /></Field>
      )}
      <Field label="Default fees (added to a new estimate, and when an estimate switches to this tariff)">
        <DefaultFeesPicker fees={fees} value={defaults} onChange={setDefaults} />
      </Field>
    </Modal>
  );
}

/** "24%", "$195.00", "$5.00/flight" — a fee's customer value, short. */
function feeValue(f: any): string {
  if (f?.fee_type === 'pct') return `${num(f.percentage)}%`;
  if (f?.fee_type === 'unit') return `$${num(f.rate).toFixed(2)}/${f.unit_label || 'unit'}`;
  return `$${num(f?.amount).toFixed(2)}`;
}

/**
 * A tariff's default fees (060): chosen from the fee catalog, in the order they
 * go on the estimate. Only active fees can be added; a retired one already on
 * the list shows as such (it is skipped when an estimate is built).
 */
function DefaultFeesPicker({ fees, value, onChange }: { fees: any[]; value: string[]; onChange: (ids: string[]) => void }) {
  const byId = new Map(fees.map((x) => [x.id, x]));
  const addable = fees.filter((x) => x.active !== false && !value.includes(x.id));
  const chip: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', background: T.panel2, border: `1px solid ${T.border}`, borderRadius: '6px', fontSize: '12px', color: T.text };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {value.length === 0 && <div style={{ fontSize: '11px', color: T.dim }}>None — estimates on this tariff start without fees.</div>}
      {value.map((id) => {
        const fee = byId.get(id);
        return (
          <div key={id} style={chip}>
            <span style={{ flex: 1, minWidth: 0 }}>
              {fee ? fee.name : 'A deleted fee'}
              <span style={{ color: T.muted, fontFamily: T.mono, marginLeft: '6px' }}>{fee ? feeValue(fee) : ''}</span>
              {fee?.active === false && <span style={{ color: T.amber, marginLeft: '6px' }}>retired — skipped</span>}
            </span>
            <button type="button" onClick={() => onChange(value.filter((x) => x !== id))} title="Remove from defaults" style={{ background: 'none', border: 'none', color: T.red, cursor: 'pointer', display: 'inline-flex', padding: 0 }}>
              <Icons.close size={14} />
            </button>
          </div>
        );
      })}
      {addable.length > 0 && (
        <select style={inputStyle} value="" onChange={(e) => e.target.value && onChange([...value, e.target.value])}>
          <option value="">+ Add a default fee…</option>
          {addable.map((x) => <option key={x.id} value={x.id}>{`${x.name} — ${feeValue(x)}`}</option>)}
        </select>
      )}
    </div>
  );
}

// ---- Fee modal (with contractor defaults per fee type) ---------------------

function FeeModal({ row, onSave, onClose }: { row?: any; onSave: (data: any, id?: string) => void; onClose: () => void }) {
  const [f, setF] = useState({
    name: row?.name ?? '',
    category: row?.category === 'materials' ? 'materials' : 'additional',
    fee_type: row?.fee_type ?? 'flat',
    amount: row?.amount ?? 0,
    percentage: row?.percentage ?? 0,
    rate: row?.rate ?? 0,
    unit_label: row?.unit_label ?? '',
    applied_to: row?.applied_to === 'subtotal' ? 'subtotal' : 'base',
    contractor_amount: row?.contractor_amount ?? '',
    contractor_percentage: row?.contractor_percentage ?? '',
    contractor_rate: row?.contractor_rate ?? '',
    contractor_share_pct: row?.contractor_share_pct ?? '',
  });
  const numOrNull = (v: any) => (v === '' || v == null ? null : num(v));

  const save = () => {
    // Local jobs only (§9.3): blank = use the contractor default below.
    const base: any = { name: f.name, category: f.category, fee_type: f.fee_type, contractor_share_pct: numOrNull(f.contractor_share_pct) };
    if (f.fee_type === 'flat') {
      base.amount = num(f.amount);
      base.contractor_amount = numOrNull(f.contractor_amount);
    } else if (f.fee_type === 'pct') {
      base.percentage = num(f.percentage);
      base.applied_to = f.applied_to;
      base.contractor_percentage = numOrNull(f.contractor_percentage);
    } else {
      base.rate = num(f.rate);
      base.unit_label = f.unit_label || null;
      base.contractor_rate = numOrNull(f.contractor_rate);
    }
    onSave(base, row?.id);
  };

  return (
    <Modal title={row ? 'Edit fee' : 'Add fee'} onClose={onClose} saveDisabled={!String(f.name).trim() || (String(f.contractor_share_pct).trim() !== '' && (num(f.contractor_share_pct) < 0 || num(f.contractor_share_pct) > 100))} onSave={save}>
      <Field label="Name"><input style={inputStyle} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
      <Field label="Category">
        <select style={inputStyle} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
          <option value="additional">Additional Fees</option>
          <option value="materials">Materials</option>
        </select>
      </Field>
      <Field label="Fee type">
        <select style={inputStyle} value={f.fee_type} onChange={(e) => setF({ ...f, fee_type: e.target.value })}>
          <option value="flat">Flat ($)</option>
          <option value="pct">Percentage (%)</option>
          <option value="unit">Per unit ($/unit)</option>
        </select>
      </Field>

      {f.fee_type === 'flat' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <Field label="Customer amount ($)"><input {...numberInput(1)} style={inputStyle} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
          <Field label="Contractor default ($)"><input {...numberInput(1)} placeholder="unset" style={inputStyle} value={f.contractor_amount} onChange={(e) => setF({ ...f, contractor_amount: e.target.value })} /></Field>
        </div>
      )}
      {f.fee_type === 'pct' && (
        <>
          <Field label="Applied to">
            <select style={inputStyle} value={f.applied_to} onChange={(e) => setF({ ...f, applied_to: e.target.value })}>
              <option value="base">Base</option>
              <option value="subtotal">Subtotal</option>
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <Field label="Customer percentage (%)"><input {...numberInput(0.1)} style={inputStyle} value={f.percentage} onChange={(e) => setF({ ...f, percentage: e.target.value })} /></Field>
            <Field label="Contractor default (%)"><input {...numberInput(0.1)} placeholder="unset" style={inputStyle} value={f.contractor_percentage} onChange={(e) => setF({ ...f, contractor_percentage: e.target.value })} /></Field>
          </div>
        </>
      )}
      {f.fee_type === 'unit' && (
        <>
          <Field label="Unit label (e.g. flight, box)"><input style={inputStyle} value={f.unit_label} onChange={(e) => setF({ ...f, unit_label: e.target.value })} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <Field label="Customer rate ($/unit)"><input {...numberInput(f.unit_label === 'lb' ? STEP_PER_LB : 1)} style={inputStyle} value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} /></Field>
            <Field label="Contractor default ($/unit)"><input {...numberInput(f.unit_label === 'lb' ? STEP_PER_LB : 1)} placeholder="unset" style={inputStyle} value={f.contractor_rate} onChange={(e) => setF({ ...f, contractor_rate: e.target.value })} /></Field>
          </div>
        </>
      )}
      <Field label="Contractor share (local jobs) %">
        <input {...numberInput(1)} min="0" max="100" placeholder="blank = use the contractor default" style={inputStyle} value={f.contractor_share_pct} onChange={(e) => setF({ ...f, contractor_share_pct: e.target.value })} />
      </Field>
      <div style={{ fontSize: '11px', color: T.muted, marginTop: '-4px' }}>Local jobs: the contractor gets this % of what the client pays for the fee (fuel 100, admin 0). Long distance always uses the contractor default.</div>
    </Modal>
  );
}

// ---- Contractor modal ------------------------------------------------------

function ContractorModal({ row, onSave, onClose }: { row?: any; onSave: (data: any, id?: string) => void; onClose: () => void }) {
  const [f, setF] = useState({
    name: row?.name ?? '', contact_name: row?.contact_name ?? '', email: row?.email ?? '',
    phone: row?.phone ?? '', notes: row?.notes ?? '', active: row?.active ?? true,
  });
  return (
    <Modal title={row ? 'Edit contractor' : 'Add contractor'} onClose={onClose} saveDisabled={!String(f.name).trim()}
      onSave={() => onSave({ name: f.name, contact_name: f.contact_name || null, email: f.email || null, phone: f.phone || null, notes: f.notes || null, active: f.active }, row?.id)}>
      <Field label="Company / name *"><input style={inputStyle} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <Field label="Contact name"><input style={inputStyle} value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} /></Field>
        <Field label="Phone"><input style={inputStyle} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
      </div>
      <Field label="Email"><input style={inputStyle} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
      <Field label="Notes"><input style={inputStyle} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
      <Field label="Status">
        <select style={inputStyle} value={f.active ? 'active' : 'inactive'} onChange={(e) => setF({ ...f, active: e.target.value === 'active' })}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </Field>
    </Modal>
  );
}
